/**
 * Job Family master data — Settings panel + Stack Ranking skill weightage source.
 * Role skill overrides and position links are stored in localStorage (no backend changes).
 */

import api from '../services/axios';
import { DEFAULT_ROLE_SKILL_WEIGHTS } from './jobFamilySkillWeights';
import {
  stripIndustryExperienceForInterns,
  shouldOmitIndustryExperience,
  getDefaultSkillWeightagesForTitle,
} from '../utils/stackRankingScores';

// Job family AI follows the toggle of the screen using it: JD Craft on the JD / position screens,
// Stack Ranking inside Stack Ranking. Off = every AI call below fails fast and the catalog rules +
// default skill weights answer instead, so a job family and skills are always suggested.
let jobFamilyAiEnabled = true;

export function setJobFamilyAiEnabled(enabled) {
  jobFamilyAiEnabled = enabled !== false;
}

export function isJobFamilyAiEnabled() {
  return jobFamilyAiEnabled;
}

function requireJobFamilyAi() {
  if (!jobFamilyAiEnabled) throw new Error('Job family AI is turned off');
}

const ROLE_SKILLS_STORAGE_PREFIX = 'reachout_jf_role_skills::';
const POSITION_JF_STORAGE_PREFIX = 'reachout_position_jf::';
const CUSTOM_MASTER_STORAGE_KEY = 'reachout_jf_custom_master';
const LAST_CREATED_ROLE_KEY = 'reachout_jf_last_created_role_key';

export const JOB_FAMILY_MASTER_UPDATED_EVENT = 'reachout:job-family-master-updated';

export const JOB_FAMILY_INTRO = {
  title: 'Job Family Master',
  summary:
    'A centralized repository of standardized job classifications across IT and non-IT industries. Job families improve AI matching, search filters, reporting, and candidate recommendations.',
  points: [
    'Open a sector, select a domain and role, then edit skill weights (must total 100%).',
    'Saved weights apply to new positions and Stack Ranking for matching job roles.',
    'Stack Ranking shows these weights read-only — edit them here only.',
  ],
};

/** Open Settings modal to a section (e.g. job-family) from anywhere in the app. */
export function openSettingsSection(sectionId = 'job-family') {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('reachout:open-settings', { detail: { sectionId } }));
}

/** @typedef {{ skill: string, weight: number }} SkillWeight */
/** @typedef {{ id: string, title: string, skillWeights: SkillWeight[] }} JobRole */
/** @typedef {{ id: string, name: string, roles: JobRole[] }} JobFamilyGroup */
/** @typedef {{ id: string, name: string, description: string, families: JobFamilyGroup[] }} IndustryDomain */

function slugifyId(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** @param {string} title @param {string} [id] @returns {JobRole} */
function makeRole(title, id) {
  const roleId = id || slugifyId(title);
  const weights = DEFAULT_ROLE_SKILL_WEIGHTS[roleId];
  return {
    id: roleId,
    title,
    skillWeights: weights ? weights.map((s) => ({ ...s })) : [],
  };
}

/** @param {string|{title: string, id?: string}} roleDef */
function resolveRoleDef(roleDef) {
  if (typeof roleDef === 'string') return makeRole(roleDef);
  return makeRole(roleDef.title, roleDef.id);
}

/** @param {string} name @param {(string|{title: string, id?: string})[]} roleDefs @param {string} [familyId] @returns {JobFamilyGroup} */
function makeFamily(name, roleDefs, familyId) {
  return {
    id: familyId || slugifyId(name),
    name,
    roles: roleDefs.map(resolveRoleDef),
  };
}

/** @type {IndustryDomain[]} */
const JOB_FAMILY_DOMAINS_BASE = [
  {
    id: 'it',
    name: 'Information Technology (IT)',
    description: 'Software, data, cloud, security, and technical support roles.',
    families: [
      makeFamily('Software Development', [
        'Software Engineer',
        'Senior Software Engineer',
        'Java Intern',
        'Python Intern',
        'Software Engineer Intern',
      ]),
      makeFamily('Quality Assurance', ['QA Engineer', 'Test Automation Engineer']),
      makeFamily('DevOps & Cloud', ['DevOps Engineer', 'Cloud Engineer', 'Site Reliability Engineer (SRE)'], 'devops-cloud'),
      makeFamily('Data & Analytics', [
        'Data Engineer',
        'Data Scientist',
        'Data Analyst',
        'AI Intern',
        'Machine Learning Intern',
        'Data Analyst Intern',
      ], 'data-analytics'),
      makeFamily('Architecture', ['Solution Architect']),
      makeFamily('Business Analysis', ['Business Analyst']),
      makeFamily('Project & Product Management', ['Project Manager', 'Product Manager'], 'project-product-management'),
      makeFamily('Cybersecurity', ['Cybersecurity Analyst'], 'cybersecurity'),
      makeFamily('IT Support', ['IT Support Specialist'], 'it-support'),
    ],
  },
  {
    id: 'healthcare',
    name: 'Healthcare',
    description: 'Clinical, nursing, pharmacy, diagnostics, and allied health roles.',
    families: [
      makeFamily('Medical Practice', ['Doctor/Physician', 'Surgeon']),
      makeFamily('Nursing', ['Nurse']),
      makeFamily('Pharmacy', ['Pharmacist']),
      makeFamily('Diagnostics & Laboratory', ['Medical Laboratory Technician', 'Radiologist'], 'diagnostics-laboratory'),
      makeFamily('Clinical Research', ['Clinical Research Associate']),
      makeFamily('Healthcare Administration', ['Healthcare Administrator', 'Hospital Operations Manager', 'Health Information Manager']),
      makeFamily('Medical Coding & Analytics', ['Medical Coder', 'Healthcare Data Analyst']),
      makeFamily('Allied Health', ['Physiotherapist']),
      makeFamily('Mental Health & Psychology', ['Psychologist', 'Psychiatrist', 'Counselor', 'Clinical Psychologist'], 'mental-health-psychology'),
      makeFamily('Biomedical Engineering', ['Biomedical Engineer', 'Medical Device Specialist']),
    ],
  },
  {
    id: 'petroleum',
    name: 'Petroleum',
    description: 'Exploration, drilling, reservoir, refinery, and HSE operations.',
    families: [
      makeFamily('Exploration', ['Geologist', 'Geophysicist']),
      makeFamily('Reservoir Engineering', ['Reservoir Engineer']),
      makeFamily('Drilling Operations', ['Drilling Engineer'], 'drilling-operations'),
      makeFamily('Production Operations', ['Petroleum Engineer', 'Production Engineer', 'Field Engineer']),
      makeFamily('Refining & Processing', ['Refinery Operator', { title: 'Process Engineer', id: 'petroleum-process-engineer' }], 'refining-processing'),
      makeFamily('Pipeline & Transportation', ['Pipeline Engineer']),
      makeFamily('Maintenance & Instrumentation', [{ title: 'Maintenance Engineer', id: 'petroleum-maintenance-engineer' }, 'Instrumentation Engineer'], 'maintenance-instrumentation'),
      makeFamily('HSE', ['HSE Officer']),
      makeFamily('Project & Operations Management', ['Project Engineer', { title: 'Operations Manager', id: 'petroleum-operations-manager' }], 'project-operations-management'),
    ],
  },
  {
    id: 'marine',
    name: 'Marine',
    description: 'Deck, engineering, navigation, and port operations.',
    families: [
      makeFamily('Navigation & Deck Operations', ['Ship Captain', 'Deck Officer', 'Navigation Officer']),
      makeFamily('Marine Engineering', ['Marine Engineer', 'Chief Engineer']),
      makeFamily('Port & Harbor Operations', ['Port Manager', 'Harbor Master']),
      makeFamily('Shipbuilding & Design', ['Shipyard Engineer', 'Naval Architect']),
      makeFamily('Marine Survey & Safety', ['Marine Surveyor', 'Marine Safety Officer']),
      makeFamily('Fleet & Logistics', [{ title: 'Fleet Manager', id: 'marine-fleet-manager' }, 'Logistics Coordinator', 'Cargo Operations Manager', 'Vessel Operations Executive']),
    ],
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing',
    description: 'Production, quality, maintenance, and plant operations.',
    families: [
      makeFamily('Production', ['Production Manager', 'Production Supervisor', 'Plant Manager']),
      makeFamily('Manufacturing Engineering', ['Manufacturing Engineer', 'Industrial Engineer']),
      makeFamily('Process Engineering', [{ title: 'Process Engineer', id: 'manufacturing-process-engineer' }, 'Lean Manufacturing Specialist']),
      makeFamily('Quality', ['Quality Engineer', 'Quality Assurance Manager']),
      makeFamily('Maintenance & Automation', [{ title: 'Maintenance Engineer', id: 'manufacturing-maintenance-engineer' }, 'Automation Engineer'], 'maintenance-automation'),
      makeFamily('Supply Chain & Procurement', [{ title: 'Supply Chain Manager', id: 'manufacturing-supply-chain-manager' }, 'Procurement Specialist', { title: 'Warehouse Manager', id: 'manufacturing-warehouse-manager' }]),
      makeFamily('Operations', [{ title: 'Operations Manager', id: 'manufacturing-operations-manager' }]),
    ],
  },
  {
    id: 'banking',
    name: 'Banking',
    description: 'Retail, corporate, investment banking, risk, and compliance.',
    families: [
      makeFamily('Retail Banking', ['Bank Manager', 'Branch Operations Manager', 'Relationship Manager', 'Loan Officer']),
      makeFamily('Corporate Banking', ['Corporate Banking Officer']),
      makeFamily('Investment & Wealth', ['Investment Banker', 'Wealth Manager', 'Portfolio Manager']),
      makeFamily('Finance & Treasury', ['Financial Analyst', 'Treasury Analyst']),
      makeFamily('Risk & Compliance', ['Risk Analyst', 'Compliance Officer', 'Fraud Analyst']),
      makeFamily('Credit', ['Credit Analyst']),
      makeFamily('Audit', ['Internal Auditor']),
    ],
  },
  {
    id: 'aviation',
    name: 'Aviation',
    description: 'Flight, cabin, ground, and aircraft maintenance operations.',
    families: [
      makeFamily('Flight Operations', ['Pilot', 'First Officer', 'Flight Dispatcher', 'Chief Pilot']),
      makeFamily('Cabin Services', ['Cabin Crew']),
      makeFamily('Air Traffic Management', ['Air Traffic Controller']),
      makeFamily('Aircraft Maintenance', ['Aircraft Maintenance Engineer', 'Aircraft Inspector', 'Technical Services Engineer']),
      makeFamily('Airport Operations', ['Ground Operations Executive', 'Airport Manager']),
      makeFamily('Aviation Safety & Security', ['Aviation Safety Officer', 'Aviation Security Specialist']),
      makeFamily('Aerospace Engineering', ['Aerospace Engineer']),
      makeFamily('Operations Management', ['Flight Operations Manager']),
    ],
  },
  {
    id: 'education',
    name: 'Education',
    description: 'Teaching, academic leadership, curriculum, and student services.',
    families: [
      makeFamily('Teaching', ['Teacher', 'Lecturer', 'Professor']),
      makeFamily('Academic Leadership', ['Principal', 'Dean', 'Academic Coordinator']),
      makeFamily('Curriculum & Learning', ['Curriculum Developer', 'Instructional Designer', 'Educational Technologist']),
      makeFamily('Student Services', ['Education Counselor', 'Admissions Officer']),
      makeFamily('Administration', ['School Administrator']),
      makeFamily('Training & Development', ['Training Manager', 'Learning & Development Specialist']),
      makeFamily('Research', ['Research Associate']),
    ],
  },
  {
    id: 'agriculture',
    name: 'Agriculture',
    description: 'Farm operations, crop production, livestock, and agribusiness.',
    families: [
      makeFamily('Farm Operations', ['Farm Manager', 'Farm Supervisor']),
      makeFamily('Crop Production', ['Crop Production Manager', 'Agronomist']),
      makeFamily('Agricultural Engineering', ['Agricultural Engineer', 'Irrigation Specialist', 'Precision Agriculture Specialist']),
      makeFamily('Agricultural Science', ['Agricultural Scientist', 'Soil Scientist']),
      makeFamily('Horticulture', ['Horticulturist']),
      makeFamily('Livestock Management', ['Livestock Manager']),
      makeFamily('Food Safety & Quality', ['Food Safety Officer']),
      makeFamily('Agricultural Advisory', ['Agricultural Consultant', 'Agricultural Extension Officer']),
      makeFamily('Agribusiness', ['Agri-business Manager']),
    ],
  },
  {
    id: 'logistics',
    name: 'Logistics',
    description: 'Warehouse, transportation, inventory, and supply chain.',
    families: [
      makeFamily('Supply Chain Management', [{ title: 'Supply Chain Manager', id: 'logistics-supply-chain-manager' }, 'Supply Chain Analyst']),
      makeFamily('Logistics Operations', ['Logistics Manager', 'Logistics Analyst', 'Operations Coordinator']),
      makeFamily('Warehouse Management', [{ title: 'Warehouse Manager', id: 'logistics-warehouse-manager' }]),
      makeFamily('Transportation & Fleet', ['Transportation Manager', { title: 'Fleet Manager', id: 'logistics-fleet-manager' }]),
      makeFamily('Procurement', ['Procurement Manager']),
      makeFamily('Inventory Management', ['Inventory Analyst']),
      makeFamily('Distribution', ['Distribution Manager']),
      makeFamily('Shipping & Trade', ['Shipping Coordinator', 'Import/Export Specialist']),
      makeFamily('Demand Planning', ['Demand Planner']),
      makeFamily('Fulfillment', ['Fulfillment Manager']),
    ],
  },
  {
    id: 'hotel-management',
    name: 'Hotel Management',
    description: 'Hotel operations, hospitality, F&B, housekeeping, and guest services.',
    families: [
      makeFamily('Hotel Operations', ['Hotel Manager', 'Assistant Hotel Manager']),
      makeFamily('Hospitality Management', ['Hospitality Executive', 'Guest Relations Manager'], 'hospitality-management'),
      makeFamily('Front Office Management', ['Front Office Executive', 'Reservations Executive']),
      makeFamily('Housekeeping Management', ['Housekeeping Manager']),
      makeFamily('Food & Beverage Management', ['Food & Beverage Manager', 'Chef'], 'food-beverage-management'),
      makeFamily('Restaurant Management', ['Restaurant Manager']),
      makeFamily('Resort Management', ['Hotel Manager', 'Assistant Hotel Manager'], 'resort-management'),
      makeFamily('Revenue Management', ['Revenue Manager']),
      makeFamily('Guest Relations', ['Guest Relations Executive']),
      makeFamily('Event & Banquet Management', ['Banquet Manager', 'Event Coordinator'], 'event-banquet-management'),
    ],
  },
  {
    id: 'railways',
    name: 'Railways',
    description: 'Rail operations, signalling, infrastructure, safety, and passenger services.',
    families: [
      makeFamily('Railway Operations', ['Station Manager', 'Railway Operations Executive', 'Train Operations Manager']),
      makeFamily('Railway Traffic Management', ['Traffic Controller']),
      makeFamily('Passenger Services', ['Station Manager'], 'passenger-services'),
      makeFamily('Freight Operations', [{ title: 'Logistics Coordinator', id: 'railway-logistics-coordinator' }]),
      makeFamily('Railway Signalling', ['Signalling Engineer']),
      makeFamily('Railway Infrastructure', ['Railway Engineer']),
      makeFamily('Railway Safety', [{ title: 'Safety Officer', id: 'railway-safety-officer' }]),
      makeFamily('Track Maintenance', [{ title: 'Maintenance Engineer', id: 'railway-maintenance-engineer' }]),
      makeFamily('Metro Rail Operations', ['Train Operations Manager', 'Station Manager'], 'metro-rail-operations'),
      makeFamily('Rail Network Planning', ['Railway Engineer'], 'rail-network-planning'),
    ],
  },
  {
    id: 'sales-marketing',
    name: 'Sales & Marketing',
    description: 'Sales, business development, marketing, branding, and customer acquisition.',
    families: [
      makeFamily('Sales', [
        'Sales Executive',
        'Sales Manager',
        'Sales Representative',
        'Inside Sales Executive',
        'Field Sales Executive',
        'Regional Sales Manager',
      ]),
      makeFamily('Business Development', [
        'Business Development Executive',
        'Business Development Manager',
        'Key Account Manager',
      ]),
      makeFamily('Marketing', [
        'Marketing Executive',
        'Marketing Manager',
        'Digital Marketing Specialist',
        'Brand Manager',
      ]),
      makeFamily('Customer Success', [
        'Customer Success Manager',
        'Account Manager',
        'Client Relationship Manager',
      ]),
      makeFamily('Pre-Sales', [
        'Pre-Sales Consultant',
        'Solution Consultant',
      ]),
    ],
  },
  {
    id: 'human-resources',
    name: 'Human Resources',
    description: 'Talent acquisition, HR operations, learning, compensation, and employee relations.',
    families: [
      makeFamily('Talent Acquisition', ['Recruiter', 'Talent Acquisition Specialist', 'Sourcing Specialist']),
      makeFamily('HR Operations', ['HR Executive', 'HR Manager', 'HR Business Partner']),
      makeFamily('Learning & Development', ['L&D Specialist', 'Training Coordinator']),
      makeFamily('Compensation & Benefits', ['Compensation Analyst', 'Payroll Specialist']),
    ],
  },
  {
    id: 'finance-accounting',
    name: 'Finance & Accounting',
    description: 'Accounting, financial planning, audit, and tax roles outside banking.',
    families: [
      makeFamily('Accounting', ['Accountant', 'Accounts Executive', 'Senior Accountant']),
      makeFamily('Financial Planning', ['Finance Manager', 'FP&A Analyst']),
      makeFamily('Taxation', ['Tax Consultant', 'Tax Analyst']),
      makeFamily('Accounts Payable / Receivable', ['Accounts Payable Executive', 'Accounts Receivable Executive'], 'ap-ar'),
    ],
  },
];

function uniqueSlug(base, taken) {
  let id = slugifyId(base) || 'item';
  let n = 1;
  while (taken.has(id)) {
    id = `${slugifyId(base) || 'item'}-${n}`;
    n += 1;
  }
  taken.add(id);
  return id;
}

function loadCustomMaster() {
  if (typeof window === 'undefined') {
    return { extraDomains: [], extraFamilies: {}, extraRoles: {}, hiddenDomains: [] };
  }
  try {
    const raw = window.localStorage.getItem(CUSTOM_MASTER_STORAGE_KEY);
    if (!raw) return { extraDomains: [], extraFamilies: {}, extraRoles: {}, hiddenDomains: [] };
    const parsed = JSON.parse(raw);
    return {
      extraDomains: Array.isArray(parsed.extraDomains) ? parsed.extraDomains : [],
      extraFamilies: parsed.extraFamilies && typeof parsed.extraFamilies === 'object' ? parsed.extraFamilies : {},
      extraRoles: parsed.extraRoles && typeof parsed.extraRoles === 'object' ? parsed.extraRoles : {},
      hiddenDomains: Array.isArray(parsed.hiddenDomains) ? parsed.hiddenDomains : [],
    };
  } catch {
    return { extraDomains: [], extraFamilies: {}, extraRoles: {}, hiddenDomains: [] };
  }
}

function saveCustomMaster(data, detail = null) {
  if (typeof window === 'undefined') return;
  invalidateCatalogInferenceCache();
  window.localStorage.setItem(CUSTOM_MASTER_STORAGE_KEY, JSON.stringify(data));
  window.dispatchEvent(new CustomEvent(JOB_FAMILY_MASTER_UPDATED_EVENT, { detail: detail || undefined }));
}

function collectAllIds() {
  const domainIds = new Set();
  const familyIds = new Set();
  const roleIds = new Set();

  const walk = (domains) => {
    domains.forEach((domain) => {
      domainIds.add(domain.id);
      (domain.families || []).forEach((family) => {
        familyIds.add(family.id);
        (family.roles || []).forEach((role) => roleIds.add(role.id));
      });
    });
  };

  walk(JOB_FAMILY_DOMAINS_BASE);
  const custom = loadCustomMaster();
  walk(custom.extraDomains || []);
  Object.values(custom.extraFamilies || {}).forEach((families) => walk([{ id: '_', families }]));
  Object.values(custom.extraRoles || {}).forEach((byFamily) => {
    Object.values(byFamily).forEach((roles) => roles.forEach((role) => roleIds.add(role.id)));
  });

  return { domainIds, familyIds, roleIds };
}

/** Built-in + user-added domains, families, and roles. */
export function getJobFamilyDomains() {
  const custom = loadCustomMaster();
  const domains = JOB_FAMILY_DOMAINS_BASE.map((d) => ({
    ...d,
    families: (d.families || []).map((f) => ({
      ...f,
      roles: [...(f.roles || [])],
    })),
  }));

  Object.entries(custom.extraRoles || {}).forEach(([domainId, byFamily]) => {
    const domain = domains.find((d) => d.id === domainId);
    if (!domain) return;
    Object.entries(byFamily).forEach(([familyId, roles]) => {
      const family = domain.families.find((f) => f.id === familyId);
      if (family && Array.isArray(roles)) family.roles.push(...roles);
    });
  });

  Object.entries(custom.extraFamilies || {}).forEach(([domainId, families]) => {
    const domain = domains.find((d) => d.id === domainId);
    if (domain && Array.isArray(families)) domain.families.push(...families);
  });

  if (custom.extraDomains?.length) domains.push(...custom.extraDomains);

  const hidden = new Set(custom.hiddenDomains || []);
  return domains.filter((d) => !hidden.has(d.id));
}

/** @deprecated Use getJobFamilyDomains() for merged built-in + custom data. */
export const JOB_FAMILY_DOMAINS = JOB_FAMILY_DOMAINS_BASE;

export function addCustomDomain({ name }) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return null;

  const custom = loadCustomMaster();
  const { domainIds } = collectAllIds();
  const id = uniqueSlug(trimmed, domainIds);
  const domain = {
    id,
    name: trimmed,
    description: '',
    families: [],
  };
  custom.extraDomains.push(domain);
  saveCustomMaster(custom);
  return domain;
}

export function isCustomDomain(domainId) {
  if (!domainId) return false;
  const custom = loadCustomMaster();
  return (custom.extraDomains || []).some((d) => d.id === domainId);
}

function clearRoleSkillStorage(domainId, families = []) {
  if (typeof window === 'undefined') return;
  families.forEach((family) => {
    (family.roles || []).forEach((role) => {
      window.localStorage.removeItem(`${ROLE_SKILLS_STORAGE_PREFIX}${getRoleKey(domainId, family.id, role.id)}`);
    });
  });
}

export function deleteDomain(domainId) {
  if (!domainId) return false;

  const domain = getJobFamilyDomains().find((d) => d.id === domainId);
  if (!domain) return false;

  const isBuiltin = JOB_FAMILY_DOMAINS_BASE.some((d) => d.id === domainId);
  const custom = loadCustomMaster();
  const isUserDomain = (custom.extraDomains || []).some((d) => d.id === domainId);
  if (!isBuiltin && !isUserDomain) return false;

  clearRoleSkillStorage(domainId, domain.families || []);

  if (isUserDomain) {
    custom.extraDomains = custom.extraDomains.filter((d) => d.id !== domainId);
  } else if (!custom.hiddenDomains.includes(domainId)) {
    custom.hiddenDomains.push(domainId);
  }

  delete custom.extraFamilies[domainId];
  delete custom.extraRoles[domainId];
  saveCustomMaster(custom);
  return true;
}

/** @deprecated Use deleteDomain */
export function deleteCustomDomain(domainId) {
  return deleteDomain(domainId);
}

export function addCustomFamily(domainId, { name }) {
  const trimmed = String(name || '').trim();
  if (!trimmed || !domainId) return null;

  const custom = loadCustomMaster();
  const { familyIds } = collectAllIds();
  const id = uniqueSlug(trimmed, familyIds);
  const family = { id, name: trimmed, roles: [] };

  const isBuiltinDomain = JOB_FAMILY_DOMAINS_BASE.some((d) => d.id === domainId);
  const isCustomDomain = (custom.extraDomains || []).some((d) => d.id === domainId);

  if (isCustomDomain) {
    const domain = custom.extraDomains.find((d) => d.id === domainId);
    domain.families.push(family);
  } else if (isBuiltinDomain) {
    if (!custom.extraFamilies[domainId]) custom.extraFamilies[domainId] = [];
    custom.extraFamilies[domainId].push(family);
  } else {
    return null;
  }

  saveCustomMaster(custom, { sectorId: domainId, familyId: family.id });
  return family;
}

export function addCustomRole(domainId, familyId, { title }) {
  const trimmed = String(title || '').trim();
  if (!trimmed || !domainId || !familyId) return null;

  const custom = loadCustomMaster();
  const { roleIds } = collectAllIds();
  const id = uniqueSlug(trimmed, roleIds);
  const role = { id, title: trimmed, skillWeights: [] };

  const domains = getJobFamilyDomains();
  const domain = domains.find((d) => d.id === domainId);
  const family = findFamilyInSector(domainId, { familyId }) || domain?.families.find((f) => f.id === familyId);
  if (!domain || !family) return null;
  const resolvedFamilyId = family.id;

  const customDomain = (custom.extraDomains || []).find((d) => d.id === domainId);
  if (customDomain) {
    const customFamily = customDomain.families.find((f) => f.id === resolvedFamilyId);
    if (customFamily) customFamily.roles.push(role);
    else return null;
  } else if ((custom.extraFamilies[domainId] || []).some((f) => f.id === resolvedFamilyId)) {
    const customFamily = custom.extraFamilies[domainId].find((f) => f.id === resolvedFamilyId);
    customFamily.roles.push(role);
  } else if (JOB_FAMILY_DOMAINS_BASE.some((d) => d.id === domainId)) {
    if (!custom.extraRoles[domainId]) custom.extraRoles[domainId] = {};
    if (!custom.extraRoles[domainId][resolvedFamilyId]) custom.extraRoles[domainId][resolvedFamilyId] = [];
    custom.extraRoles[domainId][resolvedFamilyId].push(role);
  } else {
    return null;
  }

  const roleKey = getRoleKey(domainId, resolvedFamilyId, role.id);
  saveCustomMaster(custom, { roleKey, sectorId: domainId, familyId: resolvedFamilyId, roleId: role.id });
  rememberLastCreatedRoleKey(roleKey);
  return role;
}

export function getRoleKey(domainId, familyId, roleId) {
  return `${domainId}::${familyId}::${roleId}`;
}

export function parseRoleKey(key) {
  const [domainId, familyId, roleId] = key.split('::');
  return { domainId, familyId, roleId };
}

/** Remember the most recently created/linked job family role for Settings auto-navigation. */
export function rememberLastCreatedRoleKey(roleKey) {
  if (typeof window === 'undefined' || !roleKey) return;
  window.localStorage.setItem(LAST_CREATED_ROLE_KEY, roleKey);
}

/** Apply last-created role selection when opening Job Family settings. */
export function applyLastCreatedRoleSelection({ expandDomain, selectFamily, selectRole } = {}) {
  if (typeof window === 'undefined') return null;
  const roleKey = window.localStorage.getItem(LAST_CREATED_ROLE_KEY);
  if (!roleKey || !isValidRoleKey(roleKey)) return null;
  const { domainId, familyId, roleId } = parseRoleKey(roleKey);
  if (expandDomain) expandDomain(domainId);
  if (selectFamily) selectFamily(familyId);
  if (selectRole) selectRole(roleId);
  return roleKey;
}

export function findRoleByKey(key) {
  const { domainId, familyId, roleId } = parseRoleKey(key);
  const domain = getJobFamilyDomains().find((d) => d.id === domainId);
  const family = domain?.families.find((f) => f.id === familyId);
  const role = family?.roles.find((r) => r.id === roleId);
  return { domain, family, role };
}

export function getTotalWeight(skillWeights) {
  return (skillWeights || []).reduce((sum, s) => sum + (Number(s.weight) || 0), 0);
}

function normalizeRoleSkillsTo100(skills) {
  if (!Array.isArray(skills) || skills.length === 0) return skills;
  const raw = skills.map((s) => Math.max(0, Number(s.weight) || 0));
  const sum = raw.reduce((a, b) => a + b, 0);
  if (sum <= 0) return skills.map((s, i) => ({ ...s, weight: raw[i] }));
  if (Math.abs(sum - 100) < 0.01) {
    return skills.map((s, i) => ({ ...s, weight: raw[i] }));
  }
  const scaled = raw.map((v) => (v * 100) / sum);
  const floors = scaled.map((x) => Math.floor(x));
  let rem = 100 - floors.reduce((a, b) => a + b, 0);
  const fracIdx = scaled
    .map((x, i) => ({ i, f: x - Math.floor(x) }))
    .sort((a, b) => b.f - a.f);
  const outWeights = [...floors];
  for (let k = 0; k < rem; k += 1) {
    outWeights[fracIdx[k % fracIdx.length].i] += 1;
  }
  return skills.map((s, i) => ({ skill: s.skill, weight: outWeights[i] }));
}

function scoringParamsToRoleSkills(params, roleTitle = '') {
  const skills = (params || [])
    .map((p) => ({
      skill: String(p.parameter || p.skillName || p.skill || '').trim(),
      weight: Number(p.weight ?? p.weightage) || 0,
    }))
    .filter((s) => s.skill);
  const adjusted = stripIndustryExperienceForInterns(skills, roleTitle).map((s) => ({
    skill: s.skill || s.skillName,
    weight: Number(s.weight ?? s.weightage) || 0,
  }));
  return normalizeRoleSkillsTo100(adjusted);
}

function stripHtmlForContext(html) {
  return String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildPositionContextText({ title, jdText = '', experience = '' }) {
  const parts = [
    String(title || '').trim(),
    experience ? `experience ${experience}` : '',
    stripHtmlForContext(jdText),
  ].filter(Boolean);
  return parts.join(' ');
}

function buildAiProfileContext({ sectorDescription = '', experience = '', jdText = '' }) {
  const jdSummary = stripHtmlForContext(jdText).slice(0, 1200);
  const chunks = [];
  if (sectorDescription) chunks.push(`Sector overview: ${sectorDescription}`);
  if (experience) chunks.push(`Required experience: ${experience}`);
  if (jdSummary) chunks.push(`Job description excerpt:\n${jdSummary}`);
  return chunks.join('\n\n');
}

/** Call AI to generate skill weights for a new job family role. */
export async function generateAiRoleSkillWeights({
  roleTitle,
  sectorName = '',
  domainName = '',
  sectorDescription = '',
  experience = '',
  jdText = '',
}) {
  requireJobFamilyAi();
  const profileContext = buildAiProfileContext({ sectorDescription, experience, jdText });
  const resp = await api.post('/stack-ranking/skill-weightage/generate-for-role', {
    roleTitle,
    sectorName,
    domainName,
    sectorDescription: profileContext || sectorDescription,
    experience: String(experience || '').trim(),
    jdText: stripHtmlForContext(jdText).slice(0, 2500),
  });
  const params = resp.data?.scoring_parameters;
  if (!Array.isArray(params) || params.length === 0) {
    throw new Error('AI returned no skill weights');
  }
  return scoringParamsToRoleSkills(params, roleTitle);
}

function normalizeTitle(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function getStoredRoleSkills(roleKey) {
  if (!roleKey || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`${ROLE_SKILLS_STORAGE_PREFIX}${roleKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function saveStoredRoleSkills(roleKey, skillWeights) {
  if (!roleKey || typeof window === 'undefined') return;
  window.localStorage.setItem(
    `${ROLE_SKILLS_STORAGE_PREFIX}${roleKey}`,
    JSON.stringify(skillWeights || [])
  );
}

/** Effective skill weights for a role (localStorage override, else master default). */
export function getRoleSkills(roleKey) {
  const stored = getStoredRoleSkills(roleKey);
  if (stored?.length) return stored.map((s) => ({ skill: s.skill, weight: Number(s.weight) || 0 }));
  const { role } = findRoleByKey(roleKey);
  return (role?.skillWeights || []).map((s) => ({ ...s }));
}

export function roleSkillsToWeightages(skillWeights) {
  return (skillWeights || []).map((s) => ({
    skillName: s.skill,
    weightage: Number(s.weight) || 0,
  }));
}

const GENERIC_SKILL_NAMES = new Set([
  'technical skills match',
  'relevant experience',
  'educational background',
  'industry experience',
  'project experience',
  'communication skills',
  'academic / project experience',
  'problem solving & learning agility',
]);

/** True when skills are empty or match the generic fallback template (not role-specific). */
export function isGenericSkillWeightages(skills) {
  if (!Array.isArray(skills) || skills.length === 0) return true;
  const names = skills.map((s) =>
    String(s.skillName || s.skill || '').trim().toLowerCase()
  );
  const genericHits = names.filter((n) => GENERIC_SKILL_NAMES.has(n)).length;
  return genericHits >= Math.min(4, names.length);
}

/**
 * Ensure a role has role-specific skill weightages.
 * Generates via AI when missing/generic; falls back to designation-aware defaults.
 */
export async function ensureRoleSkillWeightages({
  roleKey,
  roleTitle = '',
  jdText = '',
  experience = '',
  forceRegenerate = false,
} = {}) {
  if (!roleKey) return [];

  const { domain, family, role } = findRoleByKey(roleKey);
  const rawTitle = String(roleTitle || role?.title || '').trim();
  // Bare titles like "java" / "hotel" resolve like full designations for AI context
  const effectiveTitle = titleForJobFamilyResolution(rawTitle) || rawTitle;
  let skills = roleSkillsToWeightages(getRoleSkills(roleKey));
  const hasIndustry = skills.some((s) => /industry\s*experience/i.test(s.skillName || ''));
  const needsGeneration =
    forceRegenerate ||
    isGenericSkillWeightages(skills) ||
    (shouldOmitIndustryExperience(effectiveTitle) && hasIndustry);

  if (!needsGeneration && skills.length) return skills;

  try {
    const aiSkills = await generateAiRoleSkillWeights({
      roleTitle: effectiveTitle,
      sectorName: domain?.name || '',
      domainName: family?.name || '',
      sectorDescription: domain?.description || '',
      experience,
      jdText,
    });
    // Always apply early-career Industry Experience safety strip on AI output
    const cleaned = stripIndustryExperienceForInterns(aiSkills, effectiveTitle).map((s) => ({
      skill: s.skill || s.skillName,
      weight: Number(s.weight ?? s.weightage) || 0,
    }));
    if (cleaned?.length && !isGenericSkillWeightages(roleSkillsToWeightages(cleaned))) {
      saveStoredRoleSkills(roleKey, cleaned);
      return roleSkillsToWeightages(getRoleSkills(roleKey));
    }
  } catch (error) {
    console.warn('AI skill weight generation failed for role:', effectiveTitle, error);
  }

  // Last resort only if AI failed — still strip Industry Experience for early-career
  const fallback = stripIndustryExperienceForInterns(
    getDefaultSkillWeightagesForTitle(effectiveTitle),
    effectiveTitle
  );
  if (fallback?.length) {
    saveStoredRoleSkills(
      roleKey,
      fallback.map((s) => ({
        skill: s.skillName || s.skill,
        weight: Number(s.weightage ?? s.weight) || 0,
      }))
    );
    return roleSkillsToWeightages(getRoleSkills(roleKey));
  }

  if (skills.length && !isGenericSkillWeightages(skills)) {
    return stripIndustryExperienceForInterns(skills, effectiveTitle);
  }
  return [];
}

/**
 * True when a role key / family name is the catch-all "Specialized Roles" dump.
 * Applies to ANY position — never keep these mappings.
 */
export function isCatchAllJobFamily(roleKeyOrName = '') {
  return /speciali[sz]ed([\s_-]*roles?)?/i.test(String(roleKeyOrName || ''));
}

/**
 * Universal skill-weightage + job-family resolver for ANY position.
 * - Re-classifies catch-all "Specialized Roles" mappings
 * - Auto-creates Sector → Domain → Designation when not in master
 * - Generates role-specific AI skill weightages (never shared generic template)
 */
/** Bare "hotel" (not hospitality) must never stay under Hospitality Management. */
function isMisplacedHotelFamily(title, familyId = '') {
  const norm = normalizeTitle(title);
  if (!/\bhotel\b/.test(norm) || /\bhospitality\b/.test(norm)) return false;
  return String(familyId || '') === 'hospitality-management';
}

/** True when linked JF designation is an auto-expanded form of the position name (hotel → Hotel Manager). */
function isExpandedDesignationMismatch(positionTitle, roleTitle) {
  const pos = String(positionTitle || '').trim();
  const role = String(roleTitle || '').trim();
  if (!pos || !role) return false;
  if (normalizeTitle(pos) === normalizeTitle(role)) return false;
  const expanded = expandShortPositionTitle(pos);
  return Boolean(expanded) && normalizeTitle(expanded) === normalizeTitle(role);
}

export async function ensurePositionSkillWeightages({
  positionId,
  positionTitle = '',
  jdText = '',
  experience = '',
  roleKey: inputRoleKey = '',
  forceRegenerate = false,
} = {}) {
  const title = String(positionTitle || '').trim();
  const resolutionTitle = titleForJobFamilyResolution(title) || title;
  let roleKey = inputRoleKey || (positionId ? resolvePositionJobFamilyKey(positionId, title) : '');

  // Drop catch-all / wrong hotel→hospitality / auto-expanded designation / wrong function domain
  if (roleKey) {
    const { family, domain, role } = findRoleByKey(roleKey);
    const forced = resolveForcedFunctionDomain(title, jdText);
    const functionMismatch =
      forced &&
      (forced.familyId !== family?.id || forced.sectorId !== domain?.id);
    if (
      isCatchAllJobFamily(roleKey) ||
      isCatchAllJobFamily(family?.name) ||
      isCatchAllJobFamily(family?.id) ||
      isMisplacedHotelFamily(title, family?.id) ||
      isExpandedDesignationMismatch(title, role?.title) ||
      functionMismatch
    ) {
      if (positionId) setPositionJobFamilyKey(positionId, null);
      roleKey = '';
    }
  }

  // Always resolve Job Family for any title when missing or force-remapping
  if ((!roleKey || forceRegenerate) && title) {
    try {
      const fetched = await autoFetchJobFamilyForPosition({
        title,
        jdText,
        experience,
        generateSkills: true,
      });
      if (fetched?.roleKey && isValidRoleKey(fetched.roleKey) && !isCatchAllJobFamily(fetched.roleKey)) {
        const { family: fetchedFamily } = findRoleByKey(fetched.roleKey);
        if (!isMisplacedHotelFamily(title, fetchedFamily?.id)) {
          roleKey = fetched.roleKey;
          if (positionId) setPositionJobFamilyKey(positionId, roleKey);
        }
      }
    } catch (error) {
      console.warn('Job family auto-fetch failed during skill ensure:', error);
    }
  }

  if (roleKey && isValidRoleKey(roleKey) && !isCatchAllJobFamily(roleKey)) {
    const { role, family } = findRoleByKey(roleKey);
    if (isCatchAllJobFamily(family?.name) || isMisplacedHotelFamily(title, family?.id)) {
      if (positionId) setPositionJobFamilyKey(positionId, null);
    } else {
      const skills = await ensureRoleSkillWeightages({
        roleKey,
        roleTitle: resolutionTitle || role?.title || title,
        jdText,
        experience,
        forceRegenerate,
      });
      return { roleKey, skills };
    }
  }

  if (!title) return { roleKey: '', skills: [] };

  // Last resort for ANY unknown title: AI skills directly from position + JD
  try {
    const aiSkills = await generateAiRoleSkillWeights({
      roleTitle: resolutionTitle,
      jdText,
      experience,
    });
    const weightages = roleSkillsToWeightages(aiSkills);
    if (weightages.length && !isGenericSkillWeightages(weightages)) {
      return { roleKey: roleKey || '', skills: weightages };
    }
  } catch (error) {
    console.warn('Direct AI skill generation failed for position:', title, error);
  }

  const fallback = getDefaultSkillWeightagesForTitle(resolutionTitle);
  return {
    roleKey: roleKey || '',
    // With AI on, an empty list asks the recruiter to fill skills in. With AI off nothing better is
    // coming, so the defaults are the suggestion rather than a blank screen.
    skills: isGenericSkillWeightages(fallback) && jobFamilyAiEnabled ? [] : fallback,
  };
}

export function weightagesToRoleSkills(weightages) {
  return (weightages || []).map((w) => ({
    skill: w.skillName,
    weight: Number(w.weightage) || 0,
  }));
}

export function getPositionJobFamilyKey(positionId) {
  if (!positionId || typeof window === 'undefined') return null;
  return window.localStorage.getItem(`${POSITION_JF_STORAGE_PREFIX}${positionId}`);
}

export function setPositionJobFamilyKey(positionId, roleKey) {
  if (!positionId || typeof window === 'undefined') return;
  if (!roleKey) {
    window.localStorage.removeItem(`${POSITION_JF_STORAGE_PREFIX}${positionId}`);
    return;
  }
  window.localStorage.setItem(`${POSITION_JF_STORAGE_PREFIX}${positionId}`, roleKey);
}

const MIN_TITLE_MATCH_SCORE = 80;
const MIN_METADATA_INFERENCE_SCORE = 60;
const MIN_SUBSTRING_MATCH_LEN = 6;

const GENERIC_TITLE_WORDS = new Set([
  'and', 'the', 'for', 'with', 'manager', 'engineer', 'analyst', 'officer', 'executive',
  'coordinator', 'specialist', 'associate', 'senior', 'junior', 'lead', 'consultant',
  'operations', 'technical', 'services', 'assistant', 'head', 'director', 'general',
  'technician', 'technologist', 'operator', 'worker', 'intern', 'internship', 'trainee',
  'developer', 'programmer',
]);

const INTERN_TECH_STACK_TOKENS = new Set([
  'java', 'python', 'javascript', 'sql', 'react', 'angular', 'node', 'dotnet',
  'golang', 'ruby', 'php', 'spring', 'android', 'ios', 'web', 'software',
  'frontend', 'backend', 'fullstack', 'csharp', 'coding', 'html', 'css',
]);

/**
 * Common abbreviation / typo aliases → canonical tokens used for Job Family resolution.
 * Designation display stays as the user typed; this is only for mapping.
 */
const TITLE_ALIAS_MAP = {
  aq: 'qa',
  'q a': 'qa',
  'q.a': 'qa',
  'q.a.': 'qa',
  ba: 'business analyst',
  bsa: 'business analyst',
  pm: 'project manager',
  po: 'product owner',
  ui: 'ui ux designer',
  ux: 'ui ux designer',
  'uiux': 'ui ux designer',
  se: 'software engineer',
  swe: 'software engineer',
  sde: 'software engineer',
  fs: 'full stack',
  fe: 'frontend',
  be: 'backend',
  de: 'data engineer',
  da: 'data analyst',
  ds: 'data scientist',
  mlops: 'machine learning',
  dba: 'database administrator',
  sysadmin: 'system administrator',
  'sys admin': 'system administrator',
};

/**
 * Function-first domain rules for ANY position.
 * First match wins — prevents AI from dumping unknown/short titles into Software Development.
 */
const FUNCTION_DOMAIN_RULES = [
  { re: /\b(qa|aq|sdet|q\.?a\.?|quality\s*assurance|test\s*automation|manual\s*test|automation\s*test|software\s*test|tester|testing)\b/, sectorId: 'it', familyId: 'quality-assurance' },
  { re: /\b(devops|sre|site\s*reliability|kubernetes|k8s|cloud\s*engineer|platform\s*engineer)\b/, sectorId: 'it', familyId: 'devops-cloud' },
  { re: /\b(cyber\s*security|cybersecurity|infosec|soc\s*analyst|security\s*analyst|penetration\s*test|pen\s*test)\b/, sectorId: 'it', familyId: 'cybersecurity' },
  { re: /\b(it\s*support|help\s*desk|service\s*desk|desktop\s*support|technical\s*support)\b/, sectorId: 'it', familyId: 'it-support' },
  { re: /\b(business\s*analyst|bsa|requirements\s*analyst)\b/, sectorId: 'it', familyId: 'business-analysis' },
  { re: /\b(solution\s*architect|enterprise\s*architect|software\s*architect|system\s*architect)\b/, sectorId: 'it', familyId: 'architecture' },
  { re: /\b(project\s*manager|product\s*manager|program\s*manager|scrum\s*master|product\s*owner)\b/, sectorId: 'it', familyId: 'project-product-management' },
  { re: /\b(data\s*scientist|data\s*engineer|data\s*analyst|machine\s*learning|ml\s*engineer|ai\s*engineer|business\s*intelligence|\bbi\b|analytics\s*engineer|data\s*harvest)\b/, sectorId: 'it', familyId: 'data-analytics' },
  { re: /\b(java|python|javascript|react|angular|node|dotnet|\.net|golang|\bgo\b|php|ruby|spring|android|ios|frontend|backend|full\s*stack|software\s*engineer|software\s*developer|programmer|coding)\b/, sectorId: 'it', familyId: 'software-development' },
  { re: /\bhotel\b(?!.*\bhospitality\b)/, sectorId: 'hotel-management', familyId: 'hotel-operations' },
  { re: /\bhospitality\b/, sectorId: 'hotel-management', familyId: 'hospitality-management' },
  { re: /\bhousekeeping\b/, sectorId: 'hotel-management', familyId: 'housekeeping-management' },
  { re: /\b(nurse|nursing|rn\b|staff\s*nurse)\b/, sectorId: 'healthcare', familyId: 'nursing' },
  { re: /\b(doctor|physician|surgeon|mbbs)\b/, sectorId: 'healthcare', familyId: 'medical-practice' },
  { re: /\b(pharmacist|pharmacy)\b/, sectorId: 'healthcare', familyId: 'pharmacy' },
  { re: /\b(sales|bde|bdm|account\s*executive)\b/, sectorId: 'sales-marketing', familyId: 'sales' },
  { re: /\b(marketing|seo|sem|brand\s*manager|digital\s*marketing)\b/, sectorId: 'sales-marketing', familyId: 'marketing' },
  { re: /\b(recruiter|talent\s*acquisition|sourcing)\b/, sectorId: 'human-resources', familyId: 'talent-acquisition' },
  { re: /\b(\bhr\b|human\s*resources|people\s*ops)\b/, sectorId: 'human-resources', familyId: 'hr-operations' },
  { re: /\b(accountant|accounts\s*payable|accounts\s*receivable|bookkeep)\b/, sectorId: 'finance-accounting', familyId: 'accounting' },
  { re: /\b(finance|fp&a|financial\s*planning|financial\s*analyst)\b/, sectorId: 'finance-accounting', familyId: 'financial-planning' },
];

/** Apply abbreviation aliases so "AQ" resolves like "QA". */
export function canonicalizePositionTitleForMapping(title) {
  const raw = String(title || '').trim();
  if (!raw) return '';
  let norm = normalizeTitle(raw);
  // Whole-title alias first (e.g. aq → qa)
  if (TITLE_ALIAS_MAP[norm]) {
    norm = TITLE_ALIAS_MAP[norm];
  } else {
    const tokens = tokenize(norm).map((t) => TITLE_ALIAS_MAP[t] || t);
    norm = tokens.join(' ');
  }
  return norm;
}

/**
 * High-confidence Sector→Domain from title (+ optional JD) using function rules.
 * Wins over weak AI guesses for ALL positions.
 */
export function resolveForcedFunctionDomain(title, jdText = '') {
  const canon = canonicalizePositionTitleForMapping(title);
  const expanded = normalizeTitle(expandShortPositionTitle(canon) || canon);
  const original = normalizeTitle(title);
  const titleHaystack = `${original} ${canon} ${expanded}`;

  const tryRules = (haystack) => {
    for (const rule of FUNCTION_DOMAIN_RULES) {
      if (!rule.re.test(haystack)) continue;
      if (!isBuiltinFamily(rule.sectorId, rule.familyId)) continue;
      const sector =
        getJobFamilyDomains().find((d) => d.id === rule.sectorId) ||
        JOB_FAMILY_DOMAINS_BASE.find((d) => d.id === rule.sectorId);
      const family = sector?.families.find((f) => f.id === rule.familyId);
      if (!sector || !family) continue;
      return {
        sectorId: rule.sectorId,
        familyId: rule.familyId,
        sector,
        family,
        score: 900,
        forced: true,
      };
    }
    return null;
  };

  // Prefer title signal first so JD keywords cannot override a clear designation
  const fromTitle = tryRules(titleHaystack);
  if (fromTitle) return fromTitle;

  // Ambiguous/short titles only: allow JD function keywords as a hint
  const titleTokens = tokenize(titleHaystack).filter((t) => t.length > 1);
  if (titleTokens.length <= 2 && jdText) {
    const jdNorm = normalizeTitle(String(jdText || '').slice(0, 1500));
    return tryRules(`${titleHaystack} ${jdNorm}`);
  }
  return null;
}

function isQualityAssuranceTitle(title) {
  const haystack = `${normalizeTitle(title)} ${canonicalizePositionTitleForMapping(title)}`;
  return /\b(qa|aq|sdet|q\.?a\.?|quality\s*assurance|test\s*automation|tester|testing)\b/.test(haystack);
}

/**
 * Expand bare/short position titles so Job Family + skill weightage resolve like full designations.
 * e.g. "java" → "Java Developer", "hotel" → "Hotel Manager", "aq" → "QA Engineer"
 * Does NOT change the user's typed position name — only used for classification / AI skills.
 */
export function expandShortPositionTitle(title) {
  const raw = String(title || '').trim();
  if (!raw) return '';

  const canon = canonicalizePositionTitleForMapping(raw);
  const norm = normalizeTitle(canon);
  const tokens = tokenize(norm);
  // Already a full designation (has role-level words or multiple meaningful tokens) — keep as-is
  const hasRoleWord = /\b(intern|internship|trainee|engineer|developer|programmer|manager|executive|officer|analyst|specialist|architect|consultant|scientist|designer|nurse|chef|pilot|teacher|accountant|recruiter)\b/.test(norm);
  if (hasRoleWord || tokens.length >= 3) {
    // Still expand known short aliases that already include role words via alias map
    if (canon !== normalizeTitle(raw) && tokens.length <= 2) {
      // fall through to expand maps using canonical form
    } else {
      return raw;
    }
  }

  const techExpand = {
    java: 'Java Developer',
    python: 'Python Developer',
    javascript: 'JavaScript Developer',
    react: 'React Developer',
    angular: 'Angular Developer',
    node: 'Node.js Developer',
    sql: 'SQL Developer',
    dotnet: '.NET Developer',
    golang: 'Go Developer',
    go: 'Go Developer',
    php: 'PHP Developer',
    ruby: 'Ruby Developer',
    android: 'Android Developer',
    ios: 'iOS Developer',
    spring: 'Java Spring Developer',
    frontend: 'Frontend Developer',
    backend: 'Backend Developer',
    fullstack: 'Full Stack Developer',
    'full stack': 'Full Stack Developer',
    devops: 'DevOps Engineer',
    qa: 'QA Engineer',
    aq: 'QA Engineer',
    sdet: 'SDET',
    ai: 'AI Engineer',
    ml: 'Machine Learning Engineer',
    'business analyst': 'Business Analyst',
    'data analyst': 'Data Analyst',
    'data engineer': 'Data Engineer',
    'data scientist': 'Data Scientist',
    'project manager': 'Project Manager',
    'product manager': 'Product Manager',
    'software engineer': 'Software Engineer',
  };

  const industryExpand = {
    hotel: 'Hotel Manager',
    hospitality: 'Hospitality Manager',
    housekeeping: 'Housekeeping Manager',
    nursing: 'Nurse',
    nurse: 'Nurse',
    sales: 'Sales Executive',
    marketing: 'Marketing Executive',
    finance: 'Finance Manager',
    accounting: 'Accountant',
    hr: 'HR Executive',
    logistics: 'Logistics Manager',
    warehouse: 'Warehouse Manager',
  };

  if (tokens.length === 1 || tokens.length === 2) {
    const key = tokens.join(' ');
    if (techExpand[key]) return techExpand[key];
    if (industryExpand[key]) return industryExpand[key];
    if (tokens.length === 1) {
      if (techExpand[tokens[0]]) return techExpand[tokens[0]];
      if (industryExpand[tokens[0]]) return industryExpand[tokens[0]];
      if (INTERN_TECH_STACK_TOKENS.has(tokens[0])) {
        return `${tokens[0].charAt(0).toUpperCase()}${tokens[0].slice(1)} Developer`;
      }
    }
  }

  return raw;
}

function titleForJobFamilyResolution(title) {
  return expandShortPositionTitle(title) || String(title || '').trim();
}

const AI_ML_TITLE_PATTERN = /\b(ai|artificial intelligence|machine learning|ml|deep learning|nlp|genai|gen ai|llm|data science|neural|computer vision)\b/;

/** Data / analytics function titles — never seat under Manufacturing/Operations or similar. */
const DATA_ANALYTICS_TITLE_PATTERN =
  /\b(data\s+(?:harvest(?:ing)?|collection|mining|warehous(?:e|ing)?|lake|pipeline|platform|quality|governance|ops|operations|engineering|analytics|analysis|integration|modeling|modelling|scientist|science)|(?:bi|business\s+intelligence)\s+(?:analyst|engineer|developer)|analytics?\s+engineer|etl\s+(?:developer|engineer)|data\s+(?:engineer|scientist|analyst)|machine\s+learning\s+engineer|ml\s+engineer)\b/;

function isAiMlTitle(title) {
  return AI_ML_TITLE_PATTERN.test(normalizeTitle(title));
}

/**
 * True for data/analytics designations (incl. "Data Harvesting Analyst").
 * Prevents wrong industry seating like Manufacturing → Operations.
 */
function isDataAnalyticsTitle(title) {
  const norm = normalizeTitle(title);
  if (!norm) return false;
  if (isAiMlTitle(title) && !isTechInternTitle(title)) return true;
  if (DATA_ANALYTICS_TITLE_PATTERN.test(norm)) return true;
  // data + … + analyst/engineer/scientist/specialist (e.g. Data Harvesting Analyst)
  if (
    /\bdata\b/.test(norm) &&
    /\b(analyst|engineer|scientist|specialist)\b/.test(norm) &&
    !/\b(database\s+administrator|dba)\b/.test(norm)
  ) {
    return true;
  }
  return false;
}

function getBuiltinItDataAnalyticsPlacement() {
  const sector = getJobFamilyDomains().find((d) => d.id === 'it') || JOB_FAMILY_DOMAINS_BASE.find((d) => d.id === 'it');
  const family = sector?.families.find((f) => f.id === 'data-analytics');
  if (!sector || !family) return null;
  return { sectorId: sector.id, familyId: family.id, sector, family };
}

function hasInternLevel(title) {
  const norm = normalizeTitle(title);
  return /\b(interns?|internship|trainee)\b/.test(norm);
}

function isAiMlInternTitle(title) {
  return hasInternLevel(title) && isAiMlTitle(title);
}

function isTechInternTitle(title) {
  if (!hasInternLevel(title)) return false;
  if (isAiMlTitle(title)) return false;
  return tokenize(normalizeTitle(title)).some((t) => INTERN_TECH_STACK_TOKENS.has(t));
}

function isSoftwareDevelopmentTitle(title) {
  const norm = normalizeTitle(title);
  const canon = canonicalizePositionTitleForMapping(title);
  if (isQualityAssuranceTitle(title) || isQualityAssuranceTitle(canon)) return false;
  if (isTechInternTitle(title)) return true;
  if (isAiMlInternTitle(title)) return false;
  if (isDataAnalyticsTitle(title)) return false;
  if (/\b(devops|sre|cyber|security|help\s*desk|business\s*analyst|architect|project\s*manager|product\s*manager)\b/.test(`${norm} ${canon}`)) {
    return false;
  }
  // Bare stack keywords (e.g. "java", "python", "react") count as software roles
  if (tokenize(canon || norm).some((t) => INTERN_TECH_STACK_TOKENS.has(t))) return true;
  return /\b(software|developer|programmer|full\s*stack|frontend|backend|web\s+developer|coding)\b/.test(`${norm} ${canon}`);
}

function isBuiltinFamily(sectorId, familyId) {
  const sector = JOB_FAMILY_DOMAINS_BASE.find((d) => d.id === sectorId);
  return Boolean(sector?.families.some((f) => f.id === familyId));
}

/** Domain name must not be derived from the position title (e.g. "Sql Intern" as domain). */
function isRoleTitleUsedAsDomainName(familyName, title) {
  const familyNorm = normalizeTitle(familyName);
  const titleNorm = normalizeTitle(title);
  if (!familyNorm || !titleNorm) return false;
  if (familyNorm === titleNorm) return true;

  const titleCore = tokenize(titleNorm).filter((t) => !GENERIC_TITLE_WORDS.has(t)).join(' ');
  const familyCore = tokenize(familyNorm).filter((t) => !GENERIC_TITLE_WORDS.has(t)).join(' ');
  if (!titleCore || !familyCore) return false;
  return titleCore === familyCore || titleCore.includes(familyCore) || familyCore.includes(titleCore);
}

function findExactRoleKeyInBuiltinFamilies(title) {
  const normalized = normalizeTitle(title);
  if (!normalized) return null;

  let found = null;
  JOB_FAMILY_DOMAINS_BASE.forEach((sector) => {
    const liveSector = getJobFamilyDomains().find((d) => d.id === sector.id) || sector;
    liveSector.families.forEach((family) => {
      if (!isBuiltinFamily(sector.id, family.id)) return;
      family.roles.forEach((role) => {
        if (normalizeTitle(role.title) === normalized) {
          found = getRoleKey(sector.id, family.id, role.id);
        }
      });
    });
  });
  return found;
}

/** Official JOB_FAMILY domains only — used for domain mapping (never custom role-named domains). */
export function buildBuiltinDomainCatalog() {
  return JOB_FAMILY_DOMAINS_BASE.flatMap((sector) =>
    sector.families.map((family) => ({
      sectorId: sector.id,
      sectorName: sector.name,
      sectorDescription: sector.description || '',
      familyId: family.id,
      familyName: family.name,
    }))
  );
}

/** AI maps position to an existing official domain (Sector → Domain). */
export async function classifyDomainFromCatalogWithAi({ title, jdText = '', experience = '' } = {}) {
  const rawTitle = String(title || '').trim();
  if (!rawTitle) return null;
  // Send expanded short titles so AI classifies "java"/"hotel" like full designations
  const roleTitle = titleForJobFamilyResolution(rawTitle) || rawTitle;

  requireJobFamilyAi();
  const resp = await api.post('/stack-ranking/job-family/classify-domain', {
    roleTitle,
    jdText: stripHtmlForContext(jdText),
    experience: String(experience || '').trim(),
    domains: buildBuiltinDomainCatalog(),
  });
  return resp.data || null;
}

/**
 * Resolve the correct existing JOB_FAMILY domain using catalog rules + AI.
 * Works for ANY position title — never locks to a wrong sector from a weak AI guess.
 */
export async function resolveCatalogDomainForPosition(title, jdText = '', { sectorId = '', experience = '' } = {}) {
  // Function-first forced mapping (QA, DevOps, Hotel, Sales, …) — beats weak AI dumps
  const forced = resolveForcedFunctionDomain(title, jdText);
  if (forced) return forced;

  // Always prefer global catalog affinity first (all sectors), not a sector-locked guess
  const globalHeuristic = findCatalogDomainForPosition(title, jdText, {});
  if (globalHeuristic && globalHeuristic.score >= 80) {
    return globalHeuristic;
  }

  if (sectorId) {
    const scoped = findCatalogDomainForPosition(title, jdText, { sectorId });
    if (scoped && scoped.score >= 80) return scoped;
  }

  if (globalHeuristic && globalHeuristic.score >= 60) return globalHeuristic;

  try {
    const ai = await classifyDomainFromCatalogWithAi({ title, jdText, experience });
    if (ai?.sectorId && ai?.familyId && isBuiltinFamily(ai.sectorId, ai.familyId)) {
      let familyId = ai.familyId;
      if (isAiMlInternTitle(title) && familyId === 'quality-assurance') {
        familyId = 'data-analytics';
      }
      // Never keep Software Development when a function rule says otherwise
      const forcedAgain = resolveForcedFunctionDomain(title, jdText);
      if (forcedAgain) return forcedAgain;

      const sector = getJobFamilyDomains().find((d) => d.id === ai.sectorId);
      const family = sector?.families.find((f) => f.id === familyId);
      if (sector && family && !isRoleTitleUsedAsDomainName(family.name, title)) {
        const aiPlacement = { sectorId: sector.id, familyId: family.id, sector, family, score: 70 };
        // Reject AI placements that have no real title support (e.g. "AQ" → Software Development)
        if (!placementHasReasonableTitleSupport(title, aiPlacement)) {
          const better = findCatalogDomainForPosition(title, jdText, {});
          if (better && better.score >= 40) return better;
          // Do not accept unsupported AI dump into software-development
          if (familyId === 'software-development' && !isSoftwareDevelopmentTitle(title)) {
            return better || null;
          }
        }
        const better = findCatalogDomainForPosition(title, jdText, {});
        if (
          better &&
          better.score >= 60 &&
          (better.sectorId !== aiPlacement.sectorId || better.familyId !== aiPlacement.familyId) &&
          !placementHasReasonableTitleSupport(title, aiPlacement)
        ) {
          return better;
        }
        if (placementHasReasonableTitleSupport(title, aiPlacement) || !better) {
          return aiPlacement;
        }
        return better;
      }
    }
  } catch (error) {
    console.warn('AI domain mapping failed:', error);
  }

  return findCatalogDomainForPosition(title, jdText, { sectorId }) || null;
}

const GENERIC_FAMILY_NAME_SET = new Set([
  'operations', 'administration', 'management', 'general', 'support', 'services',
]);

/** Soft support check — novel titles need not match every token in the catalog. */
function placementHasReasonableTitleSupport(title, { sectorId, familyId }) {
  const forced = resolveForcedFunctionDomain(title);
  if (forced) {
    return forced.sectorId === sectorId && forced.familyId === familyId;
  }

  const expanded = titleForJobFamilyResolution(title);
  const discriminators = getDiscriminativeTitleTokens(expanded);
  const sector = getJobFamilyDomains().find((d) => d.id === sectorId);
  const family = sector?.families.find((f) => f.id === familyId);
  if (!family) return false;

  const norm = normalizeTitle(expanded);
  const originalNorm = normalizeTitle(title);
  const fuzzyOk = family.roles.some((role) => {
    const roleNorm = normalizeTitle(role.title);
    return (
      scoreTitleAgainstRole(norm, roleNorm) >= 70 ||
      scoreTitleAgainstRole(originalNorm, roleNorm) >= 70
    );
  });
  if (fuzzyOk) return true;

  // Empty discriminators (very short titles like "AQ") must NOT auto-approve any AI dump
  if (!discriminators.length) return false;

  const catalogContext = catalogContextTextForPlacement({ sectorId, familyId });
  const matched = discriminators.filter((t) => catalogContext.includes(t));
  if (matched.length > 0) return true;

  // Do not treat Software Development as a default dump for unsupported titles
  if (familyId === 'software-development' && !isSoftwareDevelopmentTitle(title)) {
    return false;
  }

  return false;
}

/**
 * Find the best existing JOB_FAMILY domain for ANY position before creating a new one.
 * Searches official catalog domains across all sectors (unless soft-scoped).
 */
export function findCatalogDomainForPosition(title, jdText = '', { sectorId = '' } = {}) {
  const forced = resolveForcedFunctionDomain(title, jdText);
  if (forced && (!sectorId || forced.sectorId === sectorId)) {
    return forced;
  }

  const expanded = titleForJobFamilyResolution(title);
  const norm = normalizeTitle(expanded);
  const originalNorm = normalizeTitle(title);
  const canonNorm = canonicalizePositionTitleForMapping(title);
  if (!norm && !originalNorm) return null;

  const contextNorm = normalizeTitle(buildPositionContextText({ title: expanded, jdText, experience: '' }));
  const searchText = `${norm} ${originalNorm} ${canonNorm} ${contextNorm}`;
  const discriminators = getDiscriminativeTitleTokens(expanded);

  const sectors = getJobFamilyDomains();

  const scoreFamily = (sector, family) => {
    let score = 0;
    const familyNorm = normalizeTitle(family.name);
    const sectorNorm = normalizeTitle(sector.name);
    const roleTitles = (family.roles || []).map((r) => r.title);
    const blob = normalizeTitle([family.name, ...roleTitles].join(' '));

    // Best fuzzy match vs existing roles — score both short and expanded titles
    let bestRoleScore = 0;
    roleTitles.forEach((roleTitle) => {
      const roleNorm = normalizeTitle(roleTitle);
      const roleScore = Math.max(
        scoreTitleAgainstRole(norm, roleNorm),
        scoreTitleAgainstRole(originalNorm, roleNorm),
        scoreTitleAgainstRole(canonNorm, roleNorm)
      );
      if (roleScore > bestRoleScore) bestRoleScore = roleScore;
    });
    score += bestRoleScore;

    // Partial discriminator coverage (do NOT require every novel token)
    if (discriminators.length) {
      const matched = discriminators.filter(
        (t) => blob.includes(t) || sectorNorm.includes(t) || normalizeTitle(sector.description || '').includes(t)
      );
      matched.forEach((t) => {
        score += t.length * 10;
      });
      score += Math.round((matched.length / discriminators.length) * 100);
      if (matched.length === 0) score -= 180;
    }

    if (familyNorm.length > 4 && searchText.includes(familyNorm)) score += familyNorm.length + 40;
    if (sectorNorm.length > 4 && contextNorm.includes(sectorNorm)) score += 35;

    // Soft preference when caller scoped a sector
    if (sectorId && sector.id === sectorId) score += 25;
    if (sectorId && sector.id !== sectorId) score -= 15;

    // Generic "Operations/Admin" families lose when title has unmatched specialty tokens
    if (GENERIC_FAMILY_NAME_SET.has(familyNorm) || family.id === 'operations') {
      const matchedInFamily = discriminators.filter((t) => blob.includes(t));
      if (discriminators.length && matchedInFamily.length === 0) score -= 220;
      else score -= 40;
    }

    // Function-specific boosts (additive; still catalog-driven)
    if (sector.id === 'it') {
      if (family.id === 'software-development' && isSoftwareDevelopmentTitle(expanded)) score += 250;
      // Penalize Software Development when title is clearly another IT function
      if (family.id === 'software-development' && !isSoftwareDevelopmentTitle(expanded)) score -= 200;
      if (
        family.id === 'quality-assurance' &&
        /\b(sdet|qa|aq|qa engineer|test automation|quality assurance|tester|testing)\b/.test(
          `${norm} ${originalNorm} ${canonNorm}`
        )
      ) {
        score += 400;
      }
      if (family.id === 'data-analytics' && isDataAnalyticsTitle(expanded) && !isTechInternTitle(expanded)) score += 400;
      if (family.id === 'data-analytics' && isAiMlInternTitle(expanded)) score += 300;
      if (family.id === 'quality-assurance' && (isAiMlTitle(expanded) || isDataAnalyticsTitle(expanded))) score -= 500;
      if (family.id === 'devops-cloud' && /\b(devops|sre|cloud engineer|kubernetes|site reliability)\b/.test(norm)) score += 250;
      if (family.id === 'cybersecurity' && /\b(cybersecurity|security analyst|infosec|soc analyst)\b/.test(norm)) score += 250;
      if (family.id === 'it-support' && /\b(it support|help desk|service desk|desktop support)\b/.test(norm)) score += 250;
      if (family.id === 'business-analysis' && /\bbusiness analyst\b/.test(`${norm} ${canonNorm}`) && !isDataAnalyticsTitle(expanded)) score += 250;
      if (family.id === 'architecture' && /\b(solution architect|enterprise architect|software architect)\b/.test(norm)) score += 250;
      if (family.id === 'project-product-management' && /\b(project manager|product manager|program manager)\b/.test(`${norm} ${canonNorm}`)) score += 250;
    }

    // Hotel: bare "hotel" / Hotel Manager → Hotel Operations (NOT Hospitality Management)
    if (sector.id === 'hotel-management') {
      const isHotelTitle = /\bhotel\b/.test(originalNorm) || /\bhotel\b/.test(norm);
      const isHospitalityTitle = /\bhospitality\b/.test(originalNorm) || /\bhospitality\b/.test(norm);
      if (isHotelTitle && !isHospitalityTitle) {
        if (family.id === 'hotel-operations') score += 350;
        if (family.id === 'hospitality-management') score -= 280;
        if (family.id === 'resort-management') score -= 80;
      }
      if (isHospitalityTitle && family.id === 'hospitality-management') score += 300;
      if (/\bhousekeeping\b/.test(norm) && family.id === 'housekeeping-management') score += 300;
    }

    if (isDataAnalyticsTitle(expanded)) {
      if (sector.id !== 'it') score -= 400;
      if (family.id === 'operations' || familyNorm === 'operations') score -= 500;
      if (family.id === 'data-analytics') score += 50;
    }

    tokenize(`${norm} ${canonNorm}`).forEach((t) => {
      if (!GENERIC_TITLE_WORDS.has(t) && t.length > 2 && blob.includes(t)) score += t.length;
      if (t === 'ai' && isAiMlTitle(expanded)) {
        if (family.id === 'data-analytics') score += 120;
        if (family.id === 'quality-assurance') score -= 200;
      }
    });

    return score;
  };

  let best = null;
  let bestScore = 0;
  sectors.forEach((sector) => {
    const builtinFamilies = JOB_FAMILY_DOMAINS_BASE.find((d) => d.id === sector.id)?.families || [];
    builtinFamilies.forEach((builtinFamily) => {
      const liveSector = getJobFamilyDomains().find((d) => d.id === sector.id);
      const family = liveSector?.families.find((f) => f.id === builtinFamily.id) || builtinFamily;
      const score = scoreFamily(sector, family);
      if (score > bestScore) {
        bestScore = score;
        best = { sectorId: sector.id, familyId: family.id, sector, family, score };
      }
    });
  });

  return bestScore >= 40 ? best : null;
}

function preferExistingCatalogDomain(classified, title, jdText = '') {
  if (!classified) return classified;
  const trimmed = String(title || '').trim();
  // Search ALL sectors — do not lock to a wrong AI sector
  const catalogDomain = findCatalogDomainForPosition(trimmed, jdText, {});
  if (!catalogDomain) return classified;

  // Always correct bare hotel → Hospitality Management mis-maps
  if (isMisplacedHotelFamily(trimmed, classified.familyId) && catalogDomain.familyId === 'hotel-operations') {
    return {
      ...classified,
      sectorId: catalogDomain.sectorId,
      familyId: catalogDomain.familyId,
      roleId: null,
      roleKey: null,
      createNewFamily: false,
      suggestedFamilyName: null,
      createNewRole: true,
      // Keep user's typed designation (e.g. "hotel"), not expanded "Hotel Manager"
      suggestedRoleTitle: trimmed,
      reason: `Mapped to existing catalog domain: ${catalogDomain.family.name}.`,
    };
  }

  const samePlacement =
    classified.sectorId === catalogDomain.sectorId && classified.familyId === catalogDomain.familyId;
  if (samePlacement) return classified;

  const aiOk = placementHasReasonableTitleSupport(trimmed, {
    sectorId: classified.sectorId || '',
    familyId: classified.familyId || '',
  });
  if (aiOk && catalogDomain.score < 80) return classified;

  return {
    ...classified,
    sectorId: catalogDomain.sectorId,
    familyId: catalogDomain.familyId,
    roleId: null,
    roleKey: null,
    createNewFamily: false,
    suggestedFamilyName: null,
    createNewRole: true,
    suggestedRoleTitle: trimmed,
    reason: `Mapped to existing catalog domain: ${catalogDomain.family.name}.`,
  };
}

let catalogSectorContextTokensCache = null;

function invalidateCatalogInferenceCache() {
  catalogSectorContextTokensCache = null;
}

/** Discriminative tokens derived from JOB_FAMILY catalog — not a hardcoded sector list. */
function getCatalogSectorContextTokens() {
  if (catalogSectorContextTokensCache) return catalogSectorContextTokensCache;
  const tokens = new Set();
  getJobFamilyDomains().forEach((sector) => {
    tokenize(sector.name).forEach((t) => {
      if (!GENERIC_TITLE_WORDS.has(t) && t.length > 2) tokens.add(t);
    });
    tokenize(sector.description || '').forEach((t) => {
      if (!GENERIC_TITLE_WORDS.has(t) && t.length > 4) tokens.add(t);
    });
    sector.families.forEach((family) => {
      tokenize(family.name).forEach((t) => {
        if (!GENERIC_TITLE_WORDS.has(t) && t.length > 3) tokens.add(t);
      });
      family.roles.forEach((role) => {
        tokenize(role.title).forEach((t) => {
          if (!GENERIC_TITLE_WORDS.has(t) && t.length > 3) tokens.add(t);
        });
      });
    });
  });
  catalogSectorContextTokensCache = tokens;
  return tokens;
}

function getDiscriminativeTitleTokens(title) {
  return tokenize(cleanTitleForMatch(title)).filter((t) => !GENERIC_TITLE_WORDS.has(t) && t.length > 2);
}

function positionHasUnmatchedDiscriminators(posTokens, roleTokens) {
  const discriminators = posTokens.filter((t) => !GENERIC_TITLE_WORDS.has(t) && t.length > 2);
  if (!discriminators.length) return false;
  return discriminators.some((t) => !roleTokens.includes(t));
}

/** Existing catalog text only — never include the pending position title (avoids self-validation). */
function catalogContextTextForPlacement({ sectorId, familyId }) {
  const sector = getJobFamilyDomains().find((d) => d.id === sectorId);
  const family = findFamilyInSector(sectorId, { familyId });
  const roleTitles = family?.roles?.map((r) => r.title) || [];
  return normalizeTitle(
    [sector?.name, sector?.description, family?.name, ...roleTitles].filter(Boolean).join(' ')
  );
}

/** Title qualifier words must appear in the chosen sector/family catalog context. */
function placementSupportsTitleDiscriminators(title, { sectorId, familyId }) {
  const discriminators = getDiscriminativeTitleTokens(title);
  if (!discriminators.length) return true;
  const catalogContext = catalogContextTextForPlacement({ sectorId, familyId });
  return discriminators.every((token) => catalogContext.includes(token));
}

function discriminatorsMatchAnyCatalogFamily(title) {
  const discriminators = getDiscriminativeTitleTokens(title);
  if (!discriminators.length) return true;
  let found = false;
  getJobFamilyDomains().forEach((sector) => {
    sector.families.forEach((family) => {
      const ctx = catalogContextTextForPlacement({ sectorId: sector.id, familyId: family.id });
      if (discriminators.every((t) => ctx.includes(t))) found = true;
    });
  });
  return found;
}

/** Pick best sector for a new family when title qualifiers are not in the catalog (e.g. Automotive). */
function findBestSectorForNewFamily(title, jdText = '') {
  const contextText = buildPositionContextText({ title, jdText, experience: '' });
  const contextTokens = tokenize(normalizeTitle(contextText));
  const discriminators = getDiscriminativeTitleTokens(title);

  let bestSector = null;
  let bestScore = 0;

  getJobFamilyDomains().forEach((sector) => {
    const sectorBlob = normalizeTitle(
      [
        sector.name,
        sector.description,
        ...sector.families.flatMap((f) => [f.name, ...f.roles.map((r) => r.title)]),
      ].join(' ')
    );
    const sectorTokens = new Set(tokenize(sectorBlob));
    let score = 0;
    contextTokens.forEach((t) => {
      if (!GENERIC_TITLE_WORDS.has(t) && sectorTokens.has(t)) score += t.length;
    });
    discriminators.forEach((d) => {
      if (sectorTokens.has(d)) score += d.length * 3;
    });
    if (score > bestScore) {
      bestScore = score;
      bestSector = sector;
    }
  });

  if (bestSector && bestScore > 0) return bestSector;
  return findSectorWithBroadestEngineeringCoverage() || getJobFamilyDomains()[0];
}

function scoreBestSectorByContextOverlap(text, title = '') {
  const normalized = normalizeTitle(text);
  if (!normalized) return null;

  const textTokens = tokenize(normalized);
  const discriminators = getDiscriminativeTitleTokens(title);
  const requireCatalogMatch = discriminators.length > 0 && discriminatorsMatchAnyCatalogFamily(title);

  let best = null;
  let bestScore = 0;

  getJobFamilyDomains().forEach((sector) => {
    sector.families.forEach((family) => {
      if (title && requireCatalogMatch && !placementSupportsTitleDiscriminators(title, {
        sectorId: sector.id,
        familyId: family.id,
      })) {
        return;
      }

      const blob = normalizeTitle(
        [sector.name, sector.description, family.name, ...family.roles.map((r) => r.title)].join(' ')
      );
      const sectorTokens = new Set(tokenize(blob));
      let score = 0;
      textTokens.forEach((t) => {
        if (!GENERIC_TITLE_WORDS.has(t) && sectorTokens.has(t)) score += t.length;
      });
      if (score > bestScore) {
        bestScore = score;
        best = { sectorId: sector.id, familyId: family.id, sector, family, score };
      }
    });
  });

  return bestScore >= 5 ? best : null;
}

/** When title qualifiers match no catalog family, pick sector best aligned with title + JD text. */
function inferBestPlacementFromContext(title, jdText = '') {
  const catalogDomain = findCatalogDomainForPosition(title, jdText);
  if (catalogDomain) return catalogDomain;

  const contextText = buildPositionContextText({ title, jdText, experience: '' });
  const discriminators = getDiscriminativeTitleTokens(title);

  if (discriminators.length) {
    let best = null;
    let bestScore = 0;
    getJobFamilyDomains().forEach((sector) => {
      sector.families.forEach((family) => {
        const familyContext = normalizeTitle(
          [sector.name, sector.description, family.name, ...family.roles.map((r) => r.title)].join(' ')
        );
        if (!discriminators.every((t) => familyContext.includes(t))) return;
        const score = discriminators.reduce((sum, t) => sum + t.length, 0) + family.name.length;
        if (score > bestScore) {
          bestScore = score;
          best = { sectorId: sector.id, familyId: family.id, sector, family };
        }
      });
    });
    if (best) return best;
  }

  return (
    inferFamilyFromCatalogMetadata(contextText) ||
    scoreBestSectorByContextOverlap(contextText, title) ||
    scoreBestSectorByContextOverlap(title, title)
  );
}

/** Catalog-derived fallback: sector with the most engineering families/roles. */
function findSectorWithBroadestEngineeringCoverage() {
  let bestSector = null;
  let bestScore = 0;
  getJobFamilyDomains().forEach((sector) => {
    let score = 0;
    sector.families.forEach((family) => {
      if (normalizeTitle(family.name).includes('engineering')) score += 3;
      family.roles.forEach((role) => {
        if (normalizeTitle(role.title).includes('engineer')) score += 1;
      });
    });
    if (score > bestScore) {
      bestScore = score;
      bestSector = sector;
    }
  });
  return bestSector;
}

function cleanTitleForMatch(title) {
  return normalizeTitle(title)
    .replace(/\bjpc\b/g, '')
    .replace(/\b(permanent|contract)\s+position\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenize(value) {
  return normalizeTitle(value)
    .split(' ')
    .filter((w) => w.length > 1 && !['and', 'the', 'for', 'with'].includes(w));
}

/** Sector-specific tokens in a role title must also appear in the position title. */
function roleHasUnmatchedSectorContext(posTokens, roleTokens) {
  const sectorTokens = getCatalogSectorContextTokens();
  return roleTokens.some((token) => sectorTokens.has(token) && !posTokens.includes(token));
}

function scoreTitleAgainstRole(normalized, roleNorm) {
  if (!roleNorm) return 0;
  if (normalized === roleNorm) return 1000 + roleNorm.length;

  const posTokens = tokenize(normalized);
  const roleTokens = tokenize(roleNorm);
  if (positionHasUnmatchedDiscriminators(posTokens, roleTokens)) return 0;
  if (roleHasUnmatchedSectorContext(posTokens, roleTokens)) return 0;

  const shorter = Math.min(normalized.length, roleNorm.length);
  const longer = Math.max(normalized.length, roleNorm.length);
  if (shorter >= MIN_SUBSTRING_MATCH_LEN) {
    if (normalized.includes(roleNorm) || roleNorm.includes(normalized)) {
      if (shorter / longer >= 0.35) return 500 + roleNorm.length;
    }
  }

  if (roleTokens.length > 0) {
    const overlap = roleTokens.filter((t) => posTokens.includes(t)).length;
    const ratio = overlap / roleTokens.length;
    if (overlap >= 2 && ratio >= 0.45) return 120 + overlap * 12 + roleNorm.length;
    if (roleTokens.length >= 2) {
      const tail = roleTokens.slice(-2).join(' ');
      if (tail.length >= MIN_SUBSTRING_MATCH_LEN && normalized.includes(tail)) {
        return 90 + tail.length;
      }
    }
  }
  return 0;
}

function findBestTitleMatch(title, { minScore = MIN_TITLE_MATCH_SCORE } = {}) {
  const normalized = cleanTitleForMatch(title);
  if (!normalized) return { key: null, score: 0 };

  let bestKey = null;
  let bestScore = 0;

  getJobFamilyDomains().forEach((domain) => {
    domain.families.forEach((family) => {
      family.roles.forEach((role) => {
        const roleNorm = normalizeTitle(role.title);
        const score = scoreTitleAgainstRole(normalized, roleNorm);
        if (score > bestScore) {
          bestScore = score;
          bestKey = getRoleKey(domain.id, family.id, role.id);
        }
      });
    });
  });

  return {
    key: bestScore >= minScore ? bestKey : null,
    score: bestScore,
  };
}

/** Case-insensitive exact match on role title across all sectors/domains. */
export function findExactRoleKeyByTitle(title) {
  const normalized = normalizeTitle(title);
  if (!normalized) return null;

  let found = null;
  getJobFamilyDomains().forEach((domain) => {
    domain.families.forEach((family) => {
      family.roles.forEach((role) => {
        if (normalizeTitle(role.title) === normalized) {
          found = getRoleKey(domain.id, family.id, role.id);
        }
      });
    });
  });
  return found;
}

/** Infer sector/family by matching position text against JOB_FAMILY catalog metadata. */
function inferFamilyFromCatalogMetadata(text) {
  const normalized = cleanTitleForMatch(text);
  if (!normalized) return null;

  let best = null;
  let bestScore = 0;

  getJobFamilyDomains().forEach((sector) => {
    sector.families.forEach((family) => {
      let score = 0;
      const familyNorm = normalizeTitle(family.name);
      if (familyNorm.length >= 5 && normalized.includes(familyNorm)) {
        score += familyNorm.length + 20;
      }

      family.roles.forEach((role) => {
        const roleScore = scoreTitleAgainstRole(normalized, normalizeTitle(role.title));
        if (roleScore > score) score = roleScore;
      });

      const sectorNorm = normalizeTitle(sector.name);
      if (sectorNorm.length >= 8 && normalized.includes(sectorNorm)) {
        score += 15;
      }

      if (score > bestScore) {
        bestScore = score;
        best = { sectorId: sector.id, familyId: family.id, sector, family };
      }
    });
  });

  return bestScore >= MIN_METADATA_INFERENCE_SCORE ? best : null;
}

export function inferSectorAndFamilyFromTitle(title) {
  const match = findBestTitleMatch(title, { minScore: MIN_TITLE_MATCH_SCORE });
  if (match.key) {
    const { domain, family } = findRoleByKey(match.key);
    if (domain && family) {
      return { sectorId: domain.id, familyId: family.id, sector: domain, family };
    }
  }
  return inferFamilyFromCatalogMetadata(title);
}

function findRoleKeyInFamily(sectorId, familyId, title) {
  const normalized = normalizeTitle(title);
  if (!normalized) return null;
  const sector = getJobFamilyDomains().find((d) => d.id === sectorId);
  const family = sector?.families.find((f) => f.id === familyId);
  const role = family?.roles.find((r) => normalizeTitle(r.title) === normalized);
  return role ? getRoleKey(sectorId, familyId, role.id) : null;
}

function findBestTitleMatchInFamily(title, sectorId, familyId, { minScore = MIN_TITLE_MATCH_SCORE } = {}) {
  const normalized = cleanTitleForMatch(title);
  if (!normalized) return null;

  const sector = getJobFamilyDomains().find((d) => d.id === sectorId);
  const family = sector?.families.find((f) => f.id === familyId);
  if (!family) return null;

  let bestKey = null;
  let bestScore = 0;
  family.roles.forEach((role) => {
    const score = scoreTitleAgainstRole(normalized, normalizeTitle(role.title));
    if (score > bestScore) {
      bestScore = score;
      bestKey = getRoleKey(sectorId, familyId, role.id);
    }
  });

  return bestScore >= minScore ? bestKey : null;
}

export function inferSectorAndFamilyFromContext({ title, jdText = '', experience = '' } = {}) {
  const contextText = buildPositionContextText({ title, jdText, experience });
  return inferSectorAndFamilyFromTitle(contextText) || inferSectorAndFamilyFromTitle(title);
}

function inferSectorFromCatalogRoleTitle(title) {
  const exactKey = findExactRoleKeyByTitle(title);
  if (!exactKey) return null;
  const { domain, family } = findRoleByKey(exactKey);
  if (!domain || !family) return null;
  return { sectorId: domain.id, familyId: family.id, sector: domain, family };
}

/** High-confidence sector/domain from catalog — exact or strong fuzzy role title match only. */
export function inferPrioritySectorFromTitle(title, jdText = '') {
  const trimmed = String(title || '').trim();
  if (!trimmed) return null;

  const catalogExact = inferSectorFromCatalogRoleTitle(trimmed);
  if (catalogExact) return { ...catalogExact, confidence: 'exact' };

  const contextText = buildPositionContextText({ title: trimmed, jdText, experience: '' });
  for (const text of [contextText, trimmed]) {
    const match = findBestTitleMatch(text, { minScore: MIN_TITLE_MATCH_SCORE });
    if (match.key) {
      const { domain, family } = findRoleByKey(match.key);
      if (domain && family) {
        return {
          sectorId: domain.id,
          familyId: family.id,
          sector: domain,
          family,
          confidence: 'catalog-fuzzy',
          score: match.score,
        };
      }
    }
  }

  return null;
}

/** Correct AI classification when catalog affinity or title qualifiers contradict the chosen path. */
function correctClassificationForTitle(classified, title, jdText = '') {
  if (!classified) return classified;
  const trimmed = String(title || '').trim();

  // Universal remap: seat under the best catalog domain for ANY title when available
  const preferred = findCatalogDomainForPosition(trimmed, jdText, {});
  if (preferred && preferred.score >= 60) {
    const aiWeak = !placementHasReasonableTitleSupport(trimmed, {
      sectorId: classified.sectorId || '',
      familyId: classified.familyId || '',
    });
    const different =
      classified.sectorId !== preferred.sectorId || classified.familyId !== preferred.familyId;
    if (different && (aiWeak || preferred.score >= 80 || isDataAnalyticsTitle(trimmed))) {
      const exactInPreferred = findRoleKeyInFamily(preferred.sectorId, preferred.familyId, trimmed);
      if (exactInPreferred) {
        return {
          ...classified,
          roleKey: exactInPreferred,
          sectorId: preferred.sectorId,
          familyId: preferred.familyId,
          createNewFamily: false,
          createNewRole: false,
          suggestedRoleTitle: null,
          suggestedFamilyName: null,
          reason: `Mapped to catalog domain: ${preferred.family.name}.`,
        };
      }
      return {
        ...classified,
        roleKey: null,
        sectorId: preferred.sectorId,
        familyId: preferred.familyId,
        roleId: null,
        createNewFamily: false,
        createNewRole: true,
        suggestedRoleTitle: trimmed,
        suggestedFamilyName: null,
        reason: `Mapped to catalog domain: ${preferred.family.name}.`,
      };
    }
  }

  classified = preferExistingCatalogDomain(classified, trimmed, jdText);

  const priority = inferPrioritySectorFromTitle(trimmed, jdText);
  if (priority?.sectorId && priority.confidence) {
    const aiSector = classified.sectorId || '';
    if (aiSector && aiSector !== priority.sectorId) {
      const exactInPriority = findRoleKeyInFamily(priority.sectorId, priority.familyId, trimmed);
      if (exactInPriority) {
        return {
          ...classified,
          roleKey: exactInPriority,
          sectorId: priority.sectorId,
          familyId: priority.familyId,
          createNewFamily: false,
          createNewRole: false,
          suggestedRoleTitle: null,
          suggestedFamilyName: null,
          reason: `Title matches ${priority.sector?.name || priority.sectorId} — corrected sector.`,
        };
      }

      return {
        ...classified,
        roleKey: null,
        sectorId: priority.sectorId,
        familyId: priority.familyId,
        roleId: null,
        createNewFamily: false,
        createNewRole: true,
        suggestedRoleTitle: trimmed,
        suggestedFamilyName: null,
        reason: `Title indicates ${priority.sector?.name || priority.sectorId} — corrected from ${aiSector}.`,
      };
    }
  }

  const sectorId = classified.sectorId || '';
  if (!sectorId) return classified;

  if (
    placementSupportsTitleDiscriminators(trimmed, {
      sectorId,
      familyId: classified.familyId || '',
    })
  ) {
    return classified;
  }

  const better = inferBestPlacementFromContext(trimmed, jdText);
  const fallbackSector =
    better?.sector ||
    (!discriminatorsMatchAnyCatalogFamily(trimmed)
      ? findBestSectorForNewFamily(trimmed, jdText)
      : findSectorWithBroadestEngineeringCoverage());
  if (!fallbackSector) return classified;

  const fallbackFamilyId = better?.familyId || '';
  const catalogDomain = findCatalogDomainForPosition(trimmed, jdText, { sectorId: fallbackSector.id });
  const needsNewFamily = !catalogDomain && (
    !fallbackFamilyId ||
    !discriminatorsMatchAnyCatalogFamily(trimmed) ||
    !placementSupportsTitleDiscriminators(trimmed, {
      sectorId: fallbackSector.id,
      familyId: fallbackFamilyId,
    })
  );

  return {
    ...classified,
    roleKey: null,
    sectorId: fallbackSector.id,
    familyId: catalogDomain?.familyId || (needsNewFamily ? '' : fallbackFamilyId),
    roleId: null,
    createNewFamily: needsNewFamily,
    suggestedFamilyName: needsNewFamily ? deriveSuggestedFamilyName(trimmed) : null,
    createNewRole: true,
    suggestedRoleTitle: trimmed,
    reason: catalogDomain
      ? `Mapped to existing catalog domain: ${catalogDomain.family.name}.`
      : `Title qualifiers not supported by ${sectorId} — reassigned using catalog context.`,
  };
}

async function placeRoleInPrioritySector({ title }) {
  const trimmed = String(title || '').trim();
  if (!trimmed) return null;

  const exactKey = findExactRoleKeyByTitle(trimmed);
  if (exactKey) {
    return { roleKey: exactKey, preview: null, created: false, familyCreated: false, skillsGenerated: false };
  }

  const match = findBestTitleMatch(trimmed, { minScore: MIN_TITLE_MATCH_SCORE });
  if (match.key) {
    return { roleKey: match.key, preview: null, created: false, familyCreated: false, skillsGenerated: false };
  }

  return null;
}

/** Flat JOB_FAMILY catalog entries for AI classification (official domains only). */
export function buildJobFamilyCatalogForClassification() {
  const entries = [];
  JOB_FAMILY_DOMAINS_BASE.forEach((sector) => {
    const liveSector = getJobFamilyDomains().find((d) => d.id === sector.id);
    sector.families.forEach((builtinFamily) => {
      const family = liveSector?.families.find((f) => f.id === builtinFamily.id) || builtinFamily;
      family.roles.forEach((role) => {
        entries.push({
          roleKey: getRoleKey(sector.id, family.id, role.id),
          sectorId: sector.id,
          sectorName: sector.name,
          sectorDescription: sector.description || '',
          familyId: family.id,
          familyName: family.name,
          roleId: role.id,
          roleTitle: role.title,
        });
      });
    });
  });
  return entries;
}

/** Build sector index for AI prompt — derived from live catalog. */
export function buildSectorIndexForClassification() {
  const sectors = new Map();
  getJobFamilyDomains().forEach((sector) => {
    sectors.set(sector.id, {
      sectorId: sector.id,
      sectorName: sector.name,
      sectorDescription: sector.description || '',
      familyCount: sector.families.length,
      roleCount: sector.families.reduce((n, f) => n + f.roles.length, 0),
    });
  });
  return Array.from(sectors.values());
}

async function resolveJobFamilyViaAi({ title, jdText, experience, generateSkills }) {
  const classified = await classifyJobFamilyFromCatalog({ title, jdText, experience });
  if (!classified) return null;

  const placed = await placeRoleFromClassification({
    classified,
    title,
    jdText,
    experience,
    generateSkills,
  });
  if (placed.roleKey) return placed;

  if (placed.preview?.sectorId && placed.preview?.pendingRoleTitle) {
    const retried = await createRoleWithOptionalSkills({
      sectorId: placed.preview.sectorId,
      familyId: placed.preview.familyId || '',
      roleTitle: placed.preview.pendingRoleTitle,
      sector: getJobFamilyDomains().find((d) => d.id === placed.preview.sectorId),
      family: findFamilyInSector(placed.preview.sectorId, { familyId: placed.preview.familyId }),
      experience,
      jdText,
      generateSkills,
      familyName: deriveSuggestedFamilyName(placed.preview.pendingRoleTitle),
      createNewFamily: !findFamilyInSector(placed.preview.sectorId, { familyId: placed.preview.familyId }),
    });
    if (retried.roleKey) return retried;
  }

  return null;
}

async function resolveJobFamilyViaCatalogContext({ title, jdText, experience, generateSkills }) {
  const trimmed = String(title || '').trim();
  const better = inferBestPlacementFromContext(trimmed, jdText);
  if (!better?.sectorId) return null;

  const existing = findRoleKeyInFamily(better.sectorId, better.familyId, trimmed);
  if (existing) {
    return { roleKey: existing, preview: null, created: false, familyCreated: false, skillsGenerated: false };
  }

  const catalogDomain = findCatalogDomainForPosition(trimmed, jdText, { sectorId: better.sectorId });
  const familyId = catalogDomain?.familyId || better.familyId || '';
  const needsNewFamily = !catalogDomain && (
    !familyId ||
    !discriminatorsMatchAnyCatalogFamily(trimmed) ||
    !placementSupportsTitleDiscriminators(trimmed, {
      sectorId: better.sectorId,
      familyId,
    })
  );

  return createRoleWithOptionalSkills({
    sectorId: better.sectorId,
    familyId: needsNewFamily ? '' : familyId,
    roleTitle: trimmed,
    sector: catalogDomain?.sector || better.sector,
    family: catalogDomain?.family || (needsNewFamily ? null : better.family),
    experience,
    jdText,
    generateSkills,
    familyName: deriveSuggestedFamilyName(trimmed),
    createNewFamily: needsNewFamily,
  });
}

/** Guaranteed role creation for ANY title when AI and catalog inference both fail.
 * Always builds a real Sector → Domain → Designation path (never Specialized Roles).
 */
async function ensureJobFamilyRoleExists({ title, jdText, experience, generateSkills }) {
  const trimmed = String(title || '').trim();
  if (!trimmed) {
    return { roleKey: '', preview: null, created: false, familyCreated: false, skillsGenerated: false };
  }

  const better = inferBestPlacementFromContext(trimmed, jdText);
  const catalogDomain = findCatalogDomainForPosition(trimmed, jdText);
  const sector =
    catalogDomain?.sector ||
    better?.sector ||
    findBestSectorForNewFamily(trimmed, jdText) ||
    getJobFamilyDomains()[0];
  if (!sector) {
    return { roleKey: '', preview: null, created: false, familyCreated: false, skillsGenerated: false };
  }

  // Only reuse an existing domain when catalog explicitly matched the role function.
  // For ANY unknown position, create a meaningful new domain from the title (never Specialized Roles).
  const familyId = catalogDomain?.familyId || '';
  const needsNewFamily = !catalogDomain;

  return createRoleWithOptionalSkills({
    sectorId: sector.id,
    familyId: needsNewFamily ? '' : familyId,
    roleTitle: trimmed,
    sector,
    family: catalogDomain?.family || null,
    experience,
    jdText,
    generateSkills,
    familyName: deriveSuggestedFamilyName(trimmed),
    createNewFamily: needsNewFamily,
  });
}

/** Classify position into JOB_FAMILY using role + JD + experience (no fuzzy title matching). */
export async function classifyJobFamilyFromCatalog({ title, jdText = '', experience = '' } = {}) {
  const roleTitle = String(title || '').trim();
  if (!roleTitle) return null;

  requireJobFamilyAi();
  const resp = await api.post('/stack-ranking/job-family/classify', {
    roleTitle,
    jdText: stripHtmlForContext(jdText),
    experience: String(experience || '').trim(),
    catalog: buildJobFamilyCatalogForClassification(),
  });
  return resp.data || null;
}

function resolveInferredFamily(inferred) {
  if (!inferred?.sectorId || !inferred?.familyId) return null;
  const sector = getJobFamilyDomains().find((d) => d.id === inferred.sectorId);
  const family = sector?.families.find((f) => f.id === inferred.familyId);
  if (!sector || !family) return null;
  return { sectorId: sector.id, familyId: family.id, sector, family };
}

function findFamilyInSector(sectorId, { familyId, familyName, roleTitle = '' } = {}) {
  const sector = getJobFamilyDomains().find((d) => d.id === sectorId);
  if (!sector) return null;
  if (familyId) {
    const family = sector.families.find((f) => f.id === familyId);
    if (family) {
      if (
        roleTitle &&
        isRoleTitleUsedAsDomainName(family.name, roleTitle) &&
        !isBuiltinFamily(sectorId, family.id)
      ) {
        const catalogDomain = findCatalogDomainForPosition(roleTitle, '', { sectorId });
        return catalogDomain?.family || null;
      }
      return family;
    }
  }
  if (familyName) {
    const norm = normalizeTitle(familyName);
    const family = sector.families.find((f) => normalizeTitle(f.name) === norm);
    if (family) {
      if (
        roleTitle &&
        isRoleTitleUsedAsDomainName(family.name, roleTitle) &&
        !isBuiltinFamily(sectorId, family.id)
      ) {
        const catalogDomain = findCatalogDomainForPosition(roleTitle, '', { sectorId });
        return catalogDomain?.family || null;
      }
      return family;
    }
  }
  return null;
}

function deriveSuggestedFamilyName(title) {
  const catalogDomain = findCatalogDomainForPosition(title);
  if (catalogDomain?.family?.name) return catalogDomain.family.name;

  if (isAiMlTitle(title)) return 'Data & Analytics';
  if (isSoftwareDevelopmentTitle(title)) return 'Software Development';

  const norm = normalizeTitle(title);
  // Universal keyword → domain map for ANY industry (never Specialized Roles)
  const keywordDomainMap = [
    [/\b(sales|selling|revenue)\b/, 'Sales'],
    [/\b(business development|bdm|bde)\b/, 'Business Development'],
    [/\b(marketing|brand|seo|sem|digital marketing)\b/, 'Marketing'],
    [/\b(customer success|account manager|client relationship)\b/, 'Customer Success'],
    [/\b(pre[\s-]?sales|solution consultant)\b/, 'Pre-Sales'],
    [/\b(recruit|talent acquisition|sourcing)\b/, 'Talent Acquisition'],
    [/\b(human resources|\bhr\b|people ops)\b/, 'HR Operations'],
    [/\b(payroll|compensation|benefits)\b/, 'Compensation & Benefits'],
    [/\b(accountant|accounts|bookkeep)\b/, 'Accounting'],
    [/\b(finance|fp&a|financial planning)\b/, 'Financial Planning'],
    [/\b(tax|taxation)\b/, 'Taxation'],
    [/\b(legal|lawyer|advocate|counsel)\b/, 'Legal'],
    [/\b(nurse|nursing)\b/, 'Nursing'],
    [/\b(doctor|physician|surgeon)\b/, 'Medical Practice'],
    [/\b(pharmacist|pharmacy)\b/, 'Pharmacy'],
    [/\b(housekeeping)\b/, 'Housekeeping Management'],
    [/\b(hotel|hospitality|front office)\b/, 'Hotel Operations'],
    [/\b(chef|cook|culinary|food\s*&\s*beverage|f\s*&\s*b)\b/, 'Food & Beverage Management'],
    [/\b(teacher|lecturer|professor|tutor)\b/, 'Teaching'],
    [/\b(pilot|cabin crew|flight)\b/, 'Flight Operations'],
    [/\b(warehouse|inventory|logistics|supply chain)\b/, 'Logistics Operations'],
    [/\b(procurement|purchasing)\b/, 'Procurement'],
    [/\b(civil|structural)\b/, 'Civil Engineering'],
    [/\b(mechanical|electrical|electronics)\b/, 'Engineering'],
    [/\b(architect|architecture)\b/, 'Architecture'],
    [/\b(content writer|copywriter|editor)\b/, 'Content & Communications'],
    [/\b(graphic design|ui\/ux|designer)\b/, 'Design'],
    [/\b(customer support|call center|helpdesk|help desk)\b/, 'Customer Support'],
    [/\b(security guard|facility|admin assistant)\b/, 'Administration'],
    [/\b(qa|aq|quality assurance|test automation|sdet|tester|testing)\b/, 'Quality Assurance'],
    [/\b(devops|sre|kubernetes)\b/, 'DevOps & Cloud'],
    [/\b(data|analytics|bi)\b/, 'Data & Analytics'],
    [/\b(java|python|react|angular|software|developer|programmer)\b/, 'Software Development'],
  ];
  for (const [re, domainName] of keywordDomainMap) {
    if (re.test(norm) || re.test(canonicalizePositionTitleForMapping(title))) return domainName;
  }

  // Derive a meaningful domain from the title itself (never "Specialized Roles").
  const LEVEL_WORDS = new Set([
    'senior', 'junior', 'lead', 'principal', 'associate', 'assistant', 'intern',
    'internship', 'trainee', 'executive', 'executer', 'executor', 'officer',
    'specialist', 'coordinator', 'manager', 'director', 'head', 'chief', 'i', 'ii', 'iii',
  ]);
  const tokens = tokenize(norm).filter((t) => t.length > 2 && !LEVEL_WORDS.has(t) && !GENERIC_TITLE_WORDS.has(t));
  if (tokens.length >= 2) {
    return tokens
      .slice(0, 2)
      .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
      .join(' ');
  }
  if (tokens.length) {
    const primary = tokens[0];
    return primary.charAt(0).toUpperCase() + primary.slice(1);
  }

  // Last resort: use cleaned title as domain label (still role-specific, not Specialized Roles)
  const cleaned = String(title || '')
    .replace(/\b(senior|junior|lead|intern|trainee|executive|executer|manager|officer|specialist)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned) {
    return cleaned
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ')
      .slice(0, 48);
  }
  return 'General Practice';
}

/**
 * Ensure a family exists under an existing sector. Never creates a new sector.
 * Returns null only when sectorId is invalid.
 */
function ensureFamilyInSector({
  sectorId,
  familyId = '',
  familyName = '',
  createNewFamily = false,
  roleTitle = '',
} = {}) {
  const sector = getJobFamilyDomains().find((d) => d.id === sectorId);
  if (!sector) return null;

  const existing = findFamilyInSector(sectorId, { familyId, familyName });
  if (existing) {
    return { sector, family: existing, familyId: existing.id, created: false };
  }

  if (!createNewFamily) return null;

  let name = String(familyName || '').trim();
  if (!name || /^speciali[sz]ed\s+roles$/i.test(name)) {
    name = deriveSuggestedFamilyName(roleTitle || name);
  }
  if (!name || /^speciali[sz]ed\s+roles$/i.test(name)) {
    name = 'General Practice';
  }
  const created = addCustomFamily(sectorId, { name });
  if (!created) return null;

  const refreshed = getJobFamilyDomains().find((d) => d.id === sectorId);
  const family = refreshed?.families.find((f) => f.id === created.id);
  if (!family) return null;
  return { sector: refreshed || sector, family, familyId: family.id, created: true };
}

async function createRoleWithOptionalSkills({
  sectorId,
  familyId,
  roleTitle,
  sector,
  family,
  experience = '',
  jdText = '',
  generateSkills = true,
  familyName = '',
  createNewFamily = false,
}) {
  const trimmed = String(roleTitle || '').trim();
  if (!trimmed) {
    return { roleKey: '', preview: null, created: false, familyCreated: false, skillsGenerated: false };
  }

  let resolvedFamily = family;
  let resolvedFamilyId = familyId;
  let familyCreated = false;

  const familyInSector = resolvedFamilyId
    ? findFamilyInSector(sectorId, { familyId: resolvedFamilyId, familyName: familyName || family?.name })
    : null;

  if (familyInSector) {
    resolvedFamily = familyInSector;
    resolvedFamilyId = familyInSector.id;
  } else if (createNewFamily) {
    const catalogDomain = findCatalogDomainForPosition(trimmed, jdText, { sectorId });
    if (catalogDomain) {
      resolvedFamily = catalogDomain.family;
      resolvedFamilyId = catalogDomain.familyId;
      sector = catalogDomain.sector;
    } else {
      const ensured = ensureFamilyInSector({
        sectorId,
        familyId: '',
        familyName: familyName || deriveSuggestedFamilyName(trimmed),
        createNewFamily: true,
        roleTitle: trimmed,
      });
      if (!ensured) {
        return {
          roleKey: '',
          preview: { sectorId, familyId: resolvedFamilyId, pendingRoleTitle: trimmed },
          created: false,
          familyCreated: false,
          skillsGenerated: false,
        };
      }
      resolvedFamily = ensured.family;
      resolvedFamilyId = ensured.familyId;
      sector = ensured.sector;
      familyCreated = ensured.created;
    }
  } else if (!resolvedFamilyId) {
    return {
      roleKey: '',
      preview: { sectorId, familyId: '', pendingRoleTitle: trimmed },
      created: false,
      familyCreated: false,
      skillsGenerated: false,
    };
  }

  const expandedTitle = titleForJobFamilyResolution(trimmed);
  // Designation must stay as the user's typed title — never seat onto expanded catalog roles
  const existing = findRoleKeyInFamily(sectorId, resolvedFamilyId, trimmed);
  if (existing) {
    return { roleKey: existing, preview: null, created: false, familyCreated, skillsGenerated: false };
  }

  const seatTitle = trimmed;
  const skillTitle = expandedTitle || trimmed;
  const created = addCustomRole(sectorId, resolvedFamilyId, { title: seatTitle });
  if (!created) {
    return {
      roleKey: '',
      preview: { sectorId, familyId: resolvedFamilyId, pendingRoleTitle: seatTitle },
      created: false,
      familyCreated,
      skillsGenerated: false,
    };
  }

  const roleKey = getRoleKey(sectorId, resolvedFamilyId, created.id);
  let skillsGenerated = false;

  if (generateSkills) {
    try {
      const skills = await generateAiRoleSkillWeights({
        roleTitle: skillTitle,
        sectorName: sector?.name || '',
        domainName: resolvedFamily?.name || '',
        sectorDescription: sector?.description || '',
        experience,
        jdText,
      });
      if (skills?.length) {
        saveStoredRoleSkills(roleKey, skills);
        skillsGenerated = true;
      }
    } catch (error) {
      console.warn('Could not generate AI skill weights for new role:', error);
      const fallback = getDefaultSkillWeightagesForTitle(skillTitle);
      if (fallback?.length) {
        saveStoredRoleSkills(
          roleKey,
          fallback.map((s) => ({ skill: s.skillName, weight: s.weightage }))
        );
        skillsGenerated = true;
      }
    }
  }

  return { roleKey, preview: null, created: true, familyCreated, skillsGenerated };
}

function normalizeClassificationTarget(classified) {
  if (!classified?.sectorId) return null;

  const sector = getJobFamilyDomains().find(
    (d) =>
      d.id === classified.sectorId ||
      normalizeTitle(d.name) === normalizeTitle(classified.sectorId) ||
      normalizeTitle(d.name) === normalizeTitle(classified.sectorName)
  );
  if (!sector) return null;

  let family =
    findFamilyInSector(sector.id, {
      familyId: classified.familyId,
      familyName: classified.suggestedFamilyName || classified.familyName,
    }) || null;

  if (family && isRoleTitleUsedAsDomainName(family.name, classified.suggestedRoleTitle || '')) {
    family = null;
  }

  return {
    sectorId: sector.id,
    sector,
    familyId: family?.id || classified.familyId || '',
    family,
    familyName: classified.suggestedFamilyName || family?.name || '',
  };
}

async function placeRoleFromClassification({
  classified,
  title,
  jdText = '',
  experience = '',
  generateSkills = true,
}) {
  const trimmed = String(title || '').trim();
  classified = correctClassificationForTitle(classified, trimmed, jdText);
  if (!trimmed || !classified?.sectorId) {
    return { roleKey: '', preview: null, created: false, familyCreated: false, skillsGenerated: false };
  }

  const normalized = normalizeClassificationTarget(classified);
  if (!normalized) {
    return { roleKey: '', preview: null, created: false, familyCreated: false, skillsGenerated: false };
  }

  const sector = normalized.sector;
  const classifiedResolved = {
    ...classified,
    sectorId: normalized.sectorId,
    familyId: normalized.familyId,
    suggestedFamilyName: normalized.familyName || classified.suggestedFamilyName,
  };

  if (classifiedResolved.roleKey && isValidRoleKey(classifiedResolved.roleKey)) {
    const { role: matchedRole } = findRoleByKey(classifiedResolved.roleKey);
    // Never keep an expanded catalog designation (hotel → Hotel Manager)
    if (!isExpandedDesignationMismatch(trimmed, matchedRole?.title)) {
      return { roleKey: classifiedResolved.roleKey, preview: null, created: false, familyCreated: false, skillsGenerated: false };
    }
  }

  // Always seat with the user's typed position title as Designation
  const roleTitle = trimmed;
  let createNewFamily = Boolean(classifiedResolved.createNewFamily);
  let familyName = classifiedResolved.suggestedFamilyName || '';
  if (!familyName || /^speciali[sz]ed\s+roles$/i.test(familyName)) {
    familyName = deriveSuggestedFamilyName(roleTitle);
    if (!classifiedResolved.familyId || /^speciali[sz]ed/i.test(String(classifiedResolved.familyId))) {
      createNewFamily = true;
      classifiedResolved.familyId = '';
    }
  }

  let family = normalized.family;
  let familyId = normalized.familyId || '';
  let familyCreated = false;

  const catalogDomain = await resolveCatalogDomainForPosition(roleTitle, jdText, {
    sectorId: classifiedResolved.sectorId,
    experience,
  });
  if (catalogDomain) {
    family = catalogDomain.family;
    familyId = catalogDomain.familyId;
    createNewFamily = false;
  } else if (family && isRoleTitleUsedAsDomainName(family.name, roleTitle) && !isBuiltinFamily(classifiedResolved.sectorId, family.id)) {
    const remap = findCatalogDomainForPosition(roleTitle, jdText, { sectorId: classifiedResolved.sectorId });
    if (remap) {
      family = remap.family;
      familyId = remap.familyId;
      createNewFamily = false;
    }
  }

  if (!createNewFamily && familyId) {
    family = findFamilyInSector(classifiedResolved.sectorId, { familyId, familyName });
  }

  if (!family && !createNewFamily && familyName) {
    family = findFamilyInSector(classifiedResolved.sectorId, { familyName });
    if (family) familyId = family.id;
  }

  if (!family) {
    const fallbackDomain = await resolveCatalogDomainForPosition(roleTitle, jdText, {
      sectorId: classifiedResolved.sectorId,
      experience,
    });
    if (fallbackDomain) {
      family = fallbackDomain.family;
      familyId = fallbackDomain.familyId;
      createNewFamily = false;
    } else {
      const ensured = ensureFamilyInSector({
        sectorId: classifiedResolved.sectorId,
        familyId: createNewFamily ? '' : familyId,
        familyName: (createNewFamily ? familyName : familyName || deriveSuggestedFamilyName(roleTitle))
          || deriveSuggestedFamilyName(roleTitle),
        createNewFamily: createNewFamily || !familyId || !findFamilyInSector(classifiedResolved.sectorId, { familyId }),
        roleTitle,
      });
      if (!ensured) {
        return { roleKey: '', preview: null, created: false, familyCreated: false, skillsGenerated: false };
      }
      family = ensured.family;
      familyId = ensured.familyId;
      familyCreated = ensured.created;
    }
  }

  const existingRoleKey = findRoleKeyInFamily(classifiedResolved.sectorId, familyId, roleTitle);
  if (existingRoleKey) {
    return { roleKey: existingRoleKey, preview: null, created: false, familyCreated, skillsGenerated: false };
  }

  if (classifiedResolved.roleId && !classifiedResolved.createNewRole) {
    const catalogKey = getRoleKey(classifiedResolved.sectorId, familyId, classifiedResolved.roleId);
    if (isValidRoleKey(catalogKey)) {
      return { roleKey: catalogKey, preview: null, created: false, familyCreated, skillsGenerated: false };
    }
  }

  if (classifiedResolved.createNewRole || !classifiedResolved.roleId) {
    const created = await createRoleWithOptionalSkills({
      sectorId: classifiedResolved.sectorId,
      familyId,
      roleTitle,
      sector,
      family,
      experience,
      jdText,
      generateSkills,
      familyName,
      createNewFamily: false,
    });
    return { ...created, familyCreated: familyCreated || created.familyCreated };
  }

  const roleKey = getRoleKey(classifiedResolved.sectorId, familyId, classifiedResolved.roleId);
  if (isValidRoleKey(roleKey)) {
    return { roleKey, preview: null, created: false, familyCreated, skillsGenerated: false };
  }

  const fallback = await createRoleWithOptionalSkills({
    sectorId: classifiedResolved.sectorId,
    familyId,
    roleTitle,
    sector,
    family,
    experience,
    jdText,
    generateSkills,
    familyName,
    createNewFamily: false,
  });
  return { ...fallback, familyCreated: familyCreated || fallback.familyCreated };
}

/**
 * Preview sector/domain/role for JD Craft while typing a position name.
 * Uses JOB_FAMILY catalog matching — no hardcoded sector rules.
 */
export function previewJobFamilyForTitle(title) {
  const trimmed = String(title || '').trim();
  if (!trimmed) return null;

  const preferred = findCatalogDomainForPosition(trimmed, '', {});
  if (preferred && preferred.score >= 60) {
    const existing = findRoleKeyInFamily(preferred.sectorId, preferred.familyId, trimmed);
    if (existing) return { roleKey: existing };
    return {
      sectorId: preferred.sectorId,
      familyId: preferred.familyId,
      pendingRoleTitle: trimmed,
    };
  }

  const exact = findExactRoleKeyByTitle(trimmed);
  if (exact) {
    // Prefer catalog-correct domain when an older wrong seating exists
    if (preferred) {
      const existing = findRoleKeyInFamily(preferred.sectorId, preferred.familyId, trimmed);
      if (existing) return { roleKey: existing };
      const { domainId, familyId } = parseRoleKey(exact);
      if (domainId === preferred.sectorId && familyId === preferred.familyId) {
        return { roleKey: exact };
      }
    } else {
      return { roleKey: exact };
    }
  }

  const match = findBestTitleMatch(trimmed, { minScore: MIN_TITLE_MATCH_SCORE });
  if (match.key) return { roleKey: match.key };

  const inferred = inferFamilyFromCatalogMetadata(trimmed);
  if (!inferred) return null;

  const existingInFamily = findRoleKeyInFamily(inferred.sectorId, inferred.familyId, trimmed);
  if (existingInFamily) return { roleKey: existingInFamily };

  return {
    sectorId: inferred.sectorId,
    familyId: inferred.familyId,
    pendingRoleTitle: trimmed,
  };
}

/**
 * High-confidence title match for auto-suggest only (avoids wrong family on partial titles).
 */
export function suggestRoleKeyByTitle(title) {
  const exact = findExactRoleKeyByTitle(title);
  if (exact) return exact;
  return findBestTitleMatch(title, { minScore: MIN_TITLE_MATCH_SCORE }).key;
}

export function matchRoleKeyByTitle(title) {
  return findBestTitleMatch(title, { minScore: MIN_TITLE_MATCH_SCORE }).key;
}

/**
 * Ensure a JD Craft position title exists in Job Family with AI skill weights.
 * Creates sector/domain/role entry when no confident match exists.
 */
export async function ensureRoleForPosition(title, { generateSkills = true, jdText = '', experience = '' } = {}) {
  const result = await resolveJobFamilyForPositionContext({
    title,
    jdText,
    experience,
    generateSkills,
  });
  return result.roleKey || null;
}

/** Resolve and persist job family for a position; safe to call after JD is available. */
export async function persistJobFamilyForPosition({
  title,
  jdText = '',
  experience = '',
  generateSkills = true,
} = {}) {
  return autoFetchJobFamilyForPosition({ title, jdText, experience, generateSkills });
}

const EMPTY_JOB_FAMILY_RESULT = {
  roleKey: '',
  preview: null,
  created: false,
  familyCreated: false,
  skillsGenerated: false,
};

/**
 * Persist a role under sector/domain in JOB_FAMILY (creates family/role in catalog when needed).
 * Every position role must be seated here — never preview-only.
 */
export async function ensureRoleSeatedInJobFamily({
  title,
  sectorId,
  familyId = '',
  familyName = '',
  jdText = '',
  experience = '',
  generateSkills = true,
} = {}) {
  const trimmed = String(title || '').trim();
  if (!trimmed || !sectorId) return { ...EMPTY_JOB_FAMILY_RESULT };

  // Prefer correct catalog domain for ANY title (avoid wrong sector seating)
  const preferredDomain = findCatalogDomainForPosition(trimmed, jdText, {})
    || (isDataAnalyticsTitle(trimmed) ? getBuiltinItDataAnalyticsPlacement() : null);

  const targetSectorId = preferredDomain?.sectorId || sectorId;
  const targetFamilyId = preferredDomain?.familyId || familyId;

  const exactInTarget = targetFamilyId
    ? findRoleKeyInFamily(targetSectorId, targetFamilyId, trimmed)
    : null;
  if (exactInTarget) {
    return { roleKey: exactInTarget, preview: null, created: false, familyCreated: false, skillsGenerated: false };
  }

  const exact = findExactRoleKeyByTitle(trimmed);
  if (exact) {
    const { domainId, familyId: exactFamilyId } = parseRoleKey(exact);
    // Reuse only when already seated under the preferred/correct domain
    if (
      (!preferredDomain || (domainId === preferredDomain.sectorId && exactFamilyId === preferredDomain.familyId)) &&
      (!targetFamilyId || exactFamilyId === targetFamilyId)
    ) {
      return { roleKey: exact, preview: null, created: false, familyCreated: false, skillsGenerated: false };
    }
  }

  if (targetFamilyId) {
    const inFamily = findRoleKeyInFamily(targetSectorId, targetFamilyId, trimmed);
    if (inFamily) {
      return { roleKey: inFamily, preview: null, created: false, familyCreated: false, skillsGenerated: false };
    }
  }

  const catalogDomain = preferredDomain || findCatalogDomainForPosition(trimmed, jdText, { sectorId: targetSectorId });
  const resolvedFamilyId = catalogDomain?.familyId || targetFamilyId;
  const sector = catalogDomain?.sector || getJobFamilyDomains().find((d) => d.id === targetSectorId);
  if (!sector) return { ...EMPTY_JOB_FAMILY_RESULT };

  const family = catalogDomain?.family || (resolvedFamilyId ? findFamilyInSector(targetSectorId, { familyId: resolvedFamilyId }) : null);
  const needsNewFamily = !catalogDomain && !family && !resolvedFamilyId;

  return createRoleWithOptionalSkills({
    sectorId: targetSectorId,
    familyId: needsNewFamily ? '' : (resolvedFamilyId || ''),
    roleTitle: trimmed,
    sector,
    family,
    experience,
    jdText,
    generateSkills,
    familyName: familyName || family?.name || deriveSuggestedFamilyName(trimmed),
    createNewFamily: needsNewFamily,
  });
}

/** Convert preview-only classification into a persisted JOB_FAMILY role. */
async function finalizeJobFamilyResult(
  result,
  { title, jdText = '', experience = '', generateSkills = true } = {}
) {
  if (result?.roleKey && isValidRoleKey(result.roleKey)) {
    // Never finalize on Specialized Roles — rebuild a proper path for ANY position
    const { family, role } = findRoleByKey(result.roleKey);
    if (isCatchAllJobFamily(result.roleKey) || isCatchAllJobFamily(family?.name) || isCatchAllJobFamily(family?.id)) {
      const rebuilt = await ensureJobFamilyRoleExists({
        title: role?.title || title,
        jdText,
        experience,
        generateSkills,
      });
      if (rebuilt?.roleKey && isValidRoleKey(rebuilt.roleKey) && !isCatchAllJobFamily(rebuilt.roleKey)) {
        rememberLastCreatedRoleKey(rebuilt.roleKey);
        return { ...rebuilt, preview: null };
      }
    }
    rememberLastCreatedRoleKey(result.roleKey);
    if (generateSkills && !result.skillsGenerated) {
      await ensureRoleSkillWeightages({
        roleKey: result.roleKey,
        roleTitle: role?.title || title,
        jdText,
        experience,
      });
    }
    return { ...result, preview: null, skillsGenerated: result.skillsGenerated || generateSkills };
  }

  const preview = result?.preview;
  const roleTitle = String(preview?.pendingRoleTitle || title || '').trim();
  const sectorId = preview?.sectorId || result?.sectorId || '';

  if (sectorId && roleTitle) {
    const seated = await ensureRoleSeatedInJobFamily({
      title: roleTitle,
      sectorId,
      familyId: preview?.familyId || result?.familyId || '',
      familyName: preview?.familyName || result?.familyName || '',
      jdText,
      experience,
      generateSkills,
    });
    if (seated.roleKey && isValidRoleKey(seated.roleKey)) {
      return { ...seated, preview: null };
    }
  }

  if (roleTitle) {
    const guaranteed = await ensureJobFamilyRoleExists({
      title: roleTitle,
      jdText,
      experience,
      generateSkills,
    });
    if (guaranteed?.roleKey && isValidRoleKey(guaranteed.roleKey)) {
      return { ...guaranteed, preview: null };
    }
  }

  return result || { ...EMPTY_JOB_FAMILY_RESULT };
}

/**
 * Auto-fetch Job Family for any position: AI classifies role + JD, creates role/family if needed,
 * and generates skill weightage. Always persists the role into JOB_FAMILY catalog.
 */
export async function autoFetchJobFamilyForPosition({
  title,
  jdText = '',
  experience = '',
  generateSkills = true,
} = {}) {
  const result = await resolveJobFamilyForPositionContext({
    title,
    jdText,
    experience,
    generateSkills,
  });
  return finalizeJobFamilyResult(result, { title, jdText, experience, generateSkills });
}

/**
 * Resolve Job Family profile from position name + JD + experience.
 * 1) Official domain mapping (rules + AI)  2) Builtin exact match  3) AI classify  4) Guaranteed create
 */
export async function resolveJobFamilyForPositionContext({
  title,
  jdText = '',
  experience = '',
  generateSkills = true,
} = {}) {
  const trimmed = String(title || '').trim();
  if (!trimmed) {
    return { roleKey: '', preview: null, created: false, familyCreated: false, skillsGenerated: false };
  }

  // Designation stays as the typed title; expansion is used only inside skill/domain helpers
  // 1. Map to correct existing official domain first (catalog rules + AI)
  const catalogDomain = await resolveCatalogDomainForPosition(trimmed, jdText, { experience });
  if (catalogDomain) {
    // Only reuse a catalog role when the designation exactly matches what the user typed
    const existingInDomain = findRoleKeyInFamily(
      catalogDomain.sectorId,
      catalogDomain.familyId,
      trimmed
    );
    if (existingInDomain) {
      return { roleKey: existingInDomain, preview: null, created: false, familyCreated: false, skillsGenerated: false };
    }
    return createRoleWithOptionalSkills({
      sectorId: catalogDomain.sectorId,
      familyId: catalogDomain.familyId,
      roleTitle: trimmed,
      sector: catalogDomain.sector,
      family: catalogDomain.family,
      experience,
      jdText,
      generateSkills,
      createNewFamily: false,
    });
  }

  // 2. Exact match in official domains only (user's typed designation)
  const exactBuiltin = findExactRoleKeyInBuiltinFamilies(trimmed);
  if (exactBuiltin) {
    return { roleKey: exactBuiltin, preview: null, created: false, familyCreated: false, skillsGenerated: false };
  }

  // 3. AI auto-fetch — full sector/domain/role classification
  try {
    const aiResolved = await resolveJobFamilyViaAi({
      title: trimmed,
      jdText,
      experience,
      generateSkills,
    });
    if (aiResolved?.roleKey) return aiResolved;
  } catch (error) {
    console.warn('AI job family auto-fetch failed, using catalog fallback:', error);
  }

  // 3. Catalog context fallback (validated against title qualifiers)
  const catalogResolved = await resolveJobFamilyViaCatalogContext({
    title: trimmed,
    jdText,
    experience,
    generateSkills,
  });
  if (catalogResolved?.roleKey) return catalogResolved;

  // 4. Guaranteed create — EVERY unknown position gets a real Sector → Domain → Designation
  //    (never Specialized Roles; AI/title-derived domain name + AI skill weights)
  return ensureJobFamilyRoleExists({ title: trimmed, jdText, experience, generateSkills });
}

/**
 * Resolve role key when creating/linking a position from JD Craft.
 * Respects explicit user selection; otherwise matches or creates in Job Family.
 */
export async function resolveRoleKeyForPosition(
  title,
  { userSelectedKey, userTouched = false, jdText = '', experience = '' } = {}
) {
  const trimmed = String(title || '').trim();
  if (!trimmed) return null;

  if (userTouched && userSelectedKey && isValidRoleKey(userSelectedKey)) {
    return userSelectedKey;
  }

  if (userSelectedKey && isValidRoleKey(userSelectedKey)) {
    return userSelectedKey;
  }

  return ensureRoleForPosition(trimmed, { jdText, experience });
}

export function isValidRoleKey(roleKey) {
  if (!roleKey) return false;
  return Boolean(findRoleByKey(roleKey).role);
}

export function getAllRoleOptions() {
  const options = [];
  getJobFamilyDomains().forEach((domain) => {
    domain.families.forEach((family) => {
      family.roles.forEach((role) => {
        options.push({
          roleKey: getRoleKey(domain.id, family.id, role.id),
          domainId: domain.id,
          domainName: domain.name,
          familyId: family.id,
          familyName: family.name,
          roleId: role.id,
          roleTitle: role.title,
          label: `${domain.name} → ${family.name} → ${role.title}`,
        });
      });
    });
  });
  return options;
}

export function getRoleOptionsForDomain(domainId) {
  const domain = getJobFamilyDomains().find((d) => d.id === domainId);
  if (!domain) return [];
  const options = [];
  domain.families.forEach((family) => {
    family.roles.forEach((role) => {
      options.push({
        roleKey: getRoleKey(domain.id, family.id, role.id),
        familyId: family.id,
        familyName: family.name,
        roleId: role.id,
        roleTitle: role.title,
        label: `${family.name} → ${role.title}`,
      });
    });
  });
  return options;
}

export function resolvePositionJobFamilyKey(positionId, positionTitle, { persistMatch = false } = {}) {
  if (positionId) {
    const linked = getPositionJobFamilyKey(positionId);
    if (linked) {
      const { family } = findRoleByKey(linked);
      if (
        !isValidRoleKey(linked) ||
        isCatchAllJobFamily(linked) ||
        isCatchAllJobFamily(family?.name) ||
        isCatchAllJobFamily(family?.id)
      ) {
        // Drop invalid / Specialized Roles links for ANY position so AI can re-classify
        setPositionJobFamilyKey(positionId, null);
      } else {
        return linked;
      }
    }
  }

  const titleMatch = matchRoleKeyByTitle(positionTitle);
  if (titleMatch) {
    const { family } = findRoleByKey(titleMatch);
    if (isCatchAllJobFamily(titleMatch) || isCatchAllJobFamily(family?.name)) {
      return '';
    }
  }
  if (titleMatch && positionId && persistMatch) setPositionJobFamilyKey(positionId, titleMatch);
  return titleMatch || '';
}

export function loadJobFamilySkillWeightages(positionId, positionTitle) {
  const roleKey = resolvePositionJobFamilyKey(positionId, positionTitle);
  const { role } = roleKey ? findRoleByKey(roleKey) : {};
  const designationTitle = role?.title || positionTitle;

  if (!roleKey) {
    return { roleKey: '', skills: [], designationTitle: positionTitle || '' };
  }
  let skills = roleSkillsToWeightages(getRoleSkills(roleKey));
  const hasIndustry = skills.some((s) => /industry\s*experience/i.test(s.skillName || ''));
  const looksGeneric =
    skills.some((s) => /^(technical skills match|relevant experience|project experience|industry experience)$/i.test(s.skillName || ''));

  // Early-career: drop Industry Experience; generic dumps cleared so AI regenerates upstream
  if (shouldOmitIndustryExperience(designationTitle) && (hasIndustry || !skills.length || looksGeneric)) {
    if (hasIndustry && skills.length) {
      const corrected = stripIndustryExperienceForInterns(skills, designationTitle);
      const roleSkills = corrected.map((s) => ({
        skill: s.skillName || s.skill,
        weight: Number(s.weightage ?? s.weight) || 0,
      }));
      if (roleSkills.length) {
        saveStoredRoleSkills(roleKey, normalizeRoleSkillsTo100(roleSkills));
        skills = roleSkillsToWeightages(getRoleSkills(roleKey));
      }
    }
  }

  // Generic stored skills should be regenerated via ensureRoleSkillWeightages (AI).
  if (isGenericSkillWeightages(skills)) {
    skills = [];
  }

  return { roleKey, skills, designationTitle };
}

export async function syncJobFamilySkillsToJd(apiClient, jdId, skills) {
  if (!apiClient || !jdId || !skills?.length) return;
  try {
    await apiClient.put(
      `/stack-ranking/skill-weightage/jd/${jdId}`,
      skills.map((skill) => ({
        skillName: skill.skillName,
        weightage: Math.max(0, Number(skill.weightage) || 0),
      }))
    );
  } catch (error) {
    console.warn('Could not sync job family skills to JD:', error);
  }
}
